import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ConsentAuditTab from './admin/ConsentAuditTab';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import {
  Mail, Users, FileText, Search, LogOut, Loader2,
  Phone, Building, Clock, LayoutDashboard, AlertTriangle, TrendingUp, TrendingDown,
  Activity, ShieldCheck, CreditCard, UserCheck, CheckCircle2, ChevronRight,
  Download, Plus, ArrowUpRight, ChevronLeft, Menu, X, AlertCircle, MapPin, Calendar,
  Banknote, Send, RefreshCw, Eye, Briefcase, ShieldAlert, Settings, Inbox, HardHat, UserCog,
  Rocket, Globe, MousePointerClick, UserPlus, Sparkles, Target, ArrowDown, Bell,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from 'recharts';
import { formatApiError } from '../lib/apiError';
import { Logo } from '../components/Logo';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', tone: 'bg-blue-100 text-blue-800' },
  { value: 'contacted', label: 'Contacted', tone: 'bg-amber-100 text-amber-800' },
  { value: 'converted', label: 'Converted', tone: 'bg-emerald-100 text-emerald-800' },
  { value: 'not_interested', label: 'Not interested', tone: 'bg-slate-100 text-slate-700' },
];

function StatusBadge({ value }) {
  const opt = STATUS_OPTIONS.find((o) => o.value === value) || { label: value, tone: 'bg-slate-100 text-slate-700' };
  return <Badge className={`${opt.tone} text-xs font-semibold border-0`}>{opt.label}</Badge>;
}

const NAV = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard },
  { key: 'launch-week', label: 'Launch Week', icon: Rocket },
  { key: 'users', label: 'Users', icon: Users },
  { key: 'branches', label: 'Branches', icon: Building },
  { key: 'workers', label: 'Workers', icon: HardHat },
  { key: 'managers', label: 'Managers', icon: UserCog },
  { key: 'shifts', label: 'Shifts', icon: FileText },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'payments', label: 'Payments', icon: CreditCard },
  { key: 'leads', label: 'Leads', icon: Inbox },
  { key: 'data-compliance', label: 'Data & Compliance', icon: ShieldAlert },
  { key: 'incidents', label: 'Incidents', icon: AlertTriangle },
  { key: 'settings', label: 'Settings', icon: Settings },
];

export default function AdminDashboard() {
  const { api, user, logout } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Filter that gets applied when we drill from a card/widget into a panel
  const [pendingFilter, setPendingFilter] = useState({ section: null, value: null });
  // Generic detail drawer shared across panels
  const [drawer, setDrawer] = useState({ open: false, type: null, item: null });

  const jumpTo = useCallback((targetSection, filterValue = null) => {
    setPendingFilter({ section: targetSection, value: filterValue });
    setSection(targetSection);
    setSidebarOpen(false);
  }, []);

  const openDrawer = useCallback((type, item) => {
    setDrawer({ open: true, type, item });
  }, []);
  const closeDrawer = useCallback(() => setDrawer((d) => ({ ...d, open: false })), []);

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-60 bg-[#0f1b3d] text-white transform transition-transform duration-200 ease-out
                    ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full px-3 py-4">
          <div className="px-2 mb-5 flex items-center justify-between">
            <Logo size="sm" variant="light" linkTo="/" />
            <button
              className="lg:hidden p-1.5 rounded-lg hover:bg-white/10"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-2 mb-2 text-[10px] font-semibold text-white/40 uppercase tracking-[0.12em]">Workspace</div>
          <nav className="flex-1 space-y-0.5">
            {NAV.map((item) => {
              const Icon = item.icon;
              const active = section === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => { setSection(item.key); setSidebarOpen(false); }}
                  className={`group w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-all duration-150
                              ${active ? 'bg-white/[0.08] text-white' : 'text-white/55 hover:bg-white/[0.04] hover:text-white'}`}
                  data-testid={`admin-nav-${item.key}`}
                >
                  <Icon className={`h-4 w-4 shrink-0 transition-colors ${active ? 'text-white' : 'text-white/50 group-hover:text-white/80'}`} />
                  <span className="flex-1 text-left">{item.label}</span>
                  {active && <span className="h-1.5 w-1.5 rounded-full bg-[#DA291C]" />}
                </button>
              );
            })}
          </nav>

          <div className="border-t border-white/[0.08] pt-3 mt-3">
            <div className="flex items-center gap-2.5 px-2 py-1.5 mb-1.5">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#DA291C] to-[#7a1810] flex items-center justify-center text-[11px] font-bold text-white shrink-0">
                {initialsOf(user?.fullName || user?.email)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-semibold text-white truncate">{user?.fullName || 'Admin'}</p>
                <p className="text-[10.5px] text-white/45 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => { logout(); navigate('/login'); }}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium text-white/55 hover:bg-white/[0.04] hover:text-white transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-sm border-b border-slate-200">
          <div className="flex items-center justify-between px-5 lg:px-8 py-3.5">
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open sidebar"
              >
                <Menu className="h-5 w-5 text-slate-700" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-slate-900 leading-tight">
                  {NAV.find((n) => n.key === section)?.label || 'Admin'}
                </h1>
                <p className="text-xs text-slate-500 hidden sm:block">Operational command center</p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-700 border-0 font-medium">
                <span className="h-1.5 w-1.5 bg-emerald-500 rounded-full mr-1.5 animate-pulse"></span>
                Live
              </Badge>
              <Button variant="outline" size="sm" className="rounded-full text-xs h-8 border-slate-200" data-testid="admin-export-btn">
                <Download className="h-3.5 w-3.5 mr-1.5" /> Export
              </Button>
            </div>
          </div>
        </header>

        <main className="px-5 lg:px-8 py-6 lg:py-8 max-w-[1400px]">
          {section === 'overview' && <OverviewPanel api={api} onJump={jumpTo} />}
          {section === 'launch-week' && <LaunchWeekPanel api={api} />}
          {section === 'users' && <UnifiedUsersPanel api={api} onOpenDrawer={openDrawer} />}
          {section === 'notifications' && <AdminNotificationsPanel api={api} />}
          {section === 'branches' && <BranchesPanel api={api} />}
          {section === 'workers' && <UsersPanel api={api} roleFilter="worker" initialFilter={pendingFilter.section === 'workers' ? pendingFilter.value : null} onOpenDrawer={openDrawer} />}
          {section === 'managers' && <UsersPanel api={api} roleFilter="manager" initialFilter={pendingFilter.section === 'managers' ? pendingFilter.value : null} onOpenDrawer={openDrawer} />}
          {section === 'shifts' && <ShiftsPanel api={api} initialFilter={pendingFilter.section === 'shifts' ? pendingFilter.value : null} onOpenDrawer={openDrawer} />}
          {section === 'payments' && <PaymentsPanel api={api} initialFilter={pendingFilter.section === 'payments' ? pendingFilter.value : null} />}
          {section === 'leads' && <DemoRequestsPanel api={api} initialFilter={pendingFilter.section === 'leads' ? pendingFilter.value : null} onOpenDrawer={openDrawer} />}
          {section === 'data-compliance' && <DataCompliancePanel api={api} />}
          {section === 'incidents' && <IncidentsPanel api={api} />}
          {section === 'settings' && <SettingsPanel api={api} />}
        </main>
      </div>

      <DetailDrawer api={api} state={drawer} onClose={closeDrawer} />
    </div>
  );
}

// ===================== LAUNCH WEEK DASHBOARD =====================

const LAUNCH_RANGES = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 Days' },
  { key: '30d', label: 'Last 30 Days' },
  { key: '90d', label: 'Last 90 Days' },
];

function LaunchKpiCard({ icon: Icon, label, value, tone = 'slate', hint }) {
  const toneMap = {
    slate: 'bg-slate-100 text-slate-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
    sky: 'bg-sky-100 text-sky-700',
  };
  return (
    <Card className="border border-slate-200" data-testid={`launch-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[11.5px] font-semibold text-slate-500 uppercase tracking-[0.06em] leading-tight">{label}</p>
          <span className={`inline-flex items-center justify-center h-8 w-8 rounded-lg ${toneMap[tone] || toneMap.slate}`}>
            <Icon className="h-4 w-4" strokeWidth={2.2} />
          </span>
        </div>
        <p className="mt-3 text-3xl sm:text-[2rem] font-extrabold tabular-nums text-slate-900 leading-none">{value}</p>
        {hint && <p className="mt-1.5 text-[11px] text-slate-400 leading-snug">{hint}</p>}
      </CardContent>
    </Card>
  );
}

function LaunchSectionHeader({ title, sub }) {
  return (
    <div className="mb-3">
      <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.12em]">{title}</h3>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// Compact pill for a single status row inside the InstallAdoptionCard.
// Renders "Label  N (xx%)" with a thin progress bar tinted by `tone`.
function AdoptionRow({ label, count, pct, tone = 'slate', testid }) {
  const TONES = {
    emerald: 'bg-emerald-500',
    rose: 'bg-rose-500',
    amber: 'bg-amber-500',
    slate: 'bg-slate-400',
    indigo: 'bg-indigo-500',
  };
  return (
    <div className="space-y-1" data-testid={testid}>
      <div className="flex items-center justify-between text-[12.5px]">
        <span className="text-slate-700">{label}</span>
        <span className="font-semibold tabular-nums text-slate-900">
          {count}
          <span className="text-slate-400 font-normal ml-1">({pct.toFixed(1)}%)</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full ${TONES[tone] || TONES.slate} transition-all`}
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>
    </div>
  );
}

function InstallAdoptionCard({ adoption }) {
  const PLATFORMS = [
    { key: 'ios', label: 'iOS' },
    { key: 'android', label: 'Android' },
    { key: 'desktop', label: 'Desktop' },
    { key: 'other', label: 'Other' },
  ];
  const install = adoption?.install || { total: 0, percentages: {}, counts: {}, byPlatform: {} };
  const notif = adoption?.notifications || { total: 0, percentages: {}, counts: {}, byPlatform: {} };

  const pct = (num, denom) => (denom > 0 ? (num / denom) * 100 : 0);

  const renderPlatformBreakdown = (summary, statusOrder) => (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="install-platform-breakdown">
      {PLATFORMS.map((p) => {
        const row = summary.byPlatform?.[p.key] || { total: 0 };
        return (
          <div key={p.key} className="rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid={`install-platform-${p.key}`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{p.label}</span>
              <span className="text-[12.5px] font-bold text-slate-900 tabular-nums">{row.total || 0}</span>
            </div>
            <div className="space-y-0.5 text-[11px] text-slate-600">
              {statusOrder.map((s) => (
                <div key={s} className="flex items-center justify-between">
                  <span className="capitalize">{s}</span>
                  <span className="tabular-nums">
                    {row[s] || 0}
                    <span className="text-slate-400 ml-1">({pct(row[s] || 0, row.total).toFixed(0)}%)</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <Card className="border border-slate-200" data-testid="launch-install-adoption-card">
      <CardContent className="p-4 sm:p-5 space-y-5">
        {/* Install block */}
        <div>
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Add to Home Screen</p>
              <p className="text-[11px] text-slate-400">{install.total} user(s) responded</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <AdoptionRow
              label="Added to home screen"
              count={install.counts?.installed || 0}
              pct={install.percentages?.installed || 0}
              tone="emerald"
              testid="install-row-installed"
            />
            <AdoptionRow
              label="Skipped"
              count={install.counts?.skipped || 0}
              pct={install.percentages?.skipped || 0}
              tone="slate"
              testid="install-row-skipped"
            />
          </div>
          {renderPlatformBreakdown(install, ['installed', 'skipped'])}
        </div>

        <div className="border-t border-slate-100" />

        {/* Notifications block */}
        <div>
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Push Notifications</p>
              <p className="text-[11px] text-slate-400">{notif.total} user(s) responded</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            <AdoptionRow
              label="Granted"
              count={notif.counts?.granted || 0}
              pct={notif.percentages?.granted || 0}
              tone="emerald"
              testid="notif-row-granted"
            />
            <AdoptionRow
              label="Denied"
              count={notif.counts?.denied || 0}
              pct={notif.percentages?.denied || 0}
              tone="rose"
              testid="notif-row-denied"
            />
            <AdoptionRow
              label="Dismissed"
              count={notif.counts?.dismissed || 0}
              pct={notif.percentages?.dismissed || 0}
              tone="amber"
              testid="notif-row-dismissed"
            />
          </div>
          {renderPlatformBreakdown(notif, ['granted', 'denied', 'dismissed'])}
        </div>

        <p className="text-[10.5px] text-slate-400 leading-relaxed pt-1 border-t border-slate-100">
          {adoption?.note || 'Latest handled choice per user, within the selected window.'}
          {adoption?.asOf ? ` Refreshed ${new Date(adoption.asOf).toLocaleString()}.` : ''}
        </p>
      </CardContent>
    </Card>
  );
}


function LaunchWeekPanel({ api }) {
  const [range, setRange] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get(`/admin/launch-dashboard?range=${range}`);
        if (!cancel) setData(r.data);
      } catch (e) {
        if (!cancel) setError(formatApiError(e, 'Failed to load Launch Week data'));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [api, range]);

  const fmt = (n) => (n ?? 0).toLocaleString('en-GB');

  return (
    <div className="space-y-7" data-testid="launch-week-panel">
      {/* Header + time filters */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-gradient-to-br from-[#DA291C] to-[#7a160c] text-white shadow-md">
            <Rocket className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">Launch Week Dashboard</h1>
            <p className="text-[12.5px] text-slate-500">Adoption, activation and growth — demo accounts excluded.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="launch-range-filters">
          {LAUNCH_RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 h-9 rounded-full text-[12px] font-semibold border transition-colors ${
                range === r.key
                  ? 'bg-[#0f1b3d] text-white border-[#0f1b3d]'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`launch-range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <Card className="border border-slate-200"><CardContent className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></CardContent></Card>
      ) : error ? (
        <Card className="border border-amber-200 bg-amber-50/60"><CardContent className="p-4 text-[13px] text-amber-800">{error}</CardContent></Card>
      ) : data ? (
        <>
          {/* HIGHLIGHT: First Shift Activation Rate */}
          <Card className="border border-slate-200 overflow-hidden relative" data-testid="launch-highlight-card">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0B132B] via-[#0f1b3d] to-[#1e1a4d]" />
            <div className="absolute inset-0 opacity-[0.08]" style={{
              backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 70% 60%, white 1px, transparent 1px)',
              backgroundSize: '40px 40px, 32px 32px',
            }} />
            <CardContent className="relative p-6 sm:p-8 text-white">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-4 w-4 text-amber-300" />
                    <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-amber-300">Primary metric</p>
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-white">First Shift Activation Rate</h2>
                  <p className="text-[12.5px] text-white/60 mt-1 max-w-md leading-snug">
                    {data.highlight.tagline}. This is the leading indicator that ShiftCover365 is solving a real problem.
                  </p>
                </div>
                <div className="text-left lg:text-right">
                  <p className="text-6xl sm:text-7xl font-extrabold tabular-nums leading-none tracking-tight" data-testid="launch-highlight-rate">
                    {data.highlight.rate}<span className="text-3xl text-white/60 align-top ml-1">%</span>
                  </p>
                  <p className="text-[12.5px] text-white/70 mt-2 tabular-nums">
                    <span className="font-bold text-white" data-testid="launch-highlight-num">{fmt(data.highlight.numerator)}</span> of{' '}
                    <span className="font-bold text-white" data-testid="launch-highlight-denom">{fmt(data.highlight.denominator)}</span> branches
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* GROWTH */}
          <section data-testid="launch-growth-section">
            <LaunchSectionHeader title="Growth" sub="Top-of-funnel — anonymous, session-deduped" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <LaunchKpiCard icon={Globe}             label="Website Visitors"          tone="indigo"  value={fmt(data.growth.visitors)}                hint={data.growth.visitorsTotal !== data.growth.visitors ? `${fmt(data.growth.visitorsTotal)} total pageviews` : 'Unique sessions'} />
              <LaunchKpiCard icon={MousePointerClick} label="Start Free Trial Clicks"   tone="sky"     value={fmt(data.growth.pilotClicks)}             hint="Hero + pricing CTAs" />
              <LaunchKpiCard icon={UserPlus}          label="Registration Starts"       tone="amber"   value={fmt(data.growth.registrationStarts)}      hint="Reached /register" />
              <LaunchKpiCard icon={CheckCircle2}      label="Registration Completions"  tone="emerald" value={fmt(data.growth.registrationCompletions)} hint="Submitted the form" />
            </div>
          </section>

          {/* ACTIVATION */}
          <section data-testid="launch-activation-section">
            <LaunchSectionHeader title="Activation" sub="Branch-level adoption of the platform" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <LaunchKpiCard icon={Building}     label="Branches Registered"           tone="slate"   value={fmt(data.activation.branchesRegistered)}      hint="Manager accounts created" />
              <LaunchKpiCard icon={ShieldCheck}  label="Branches Onboarded"            tone="indigo"  value={fmt(data.activation.branchesOnboarded)}       hint="Completed onboarding setup" />
              <LaunchKpiCard icon={FileText}     label="Branches Posted First Shift"   tone="emerald" value={fmt(data.activation.branchesPostedFirstShift)} hint="Reached first activation step" />
              <LaunchKpiCard icon={Target}       label="First Shift Activation Rate"   tone="red"     value={`${data.activation.firstShiftActivationRate}%`} hint="Posted ÷ Registered" />
            </div>
          </section>

          {/* MARKETPLACE */}
          <section data-testid="launch-marketplace-section">
            <LaunchSectionHeader title="Marketplace Health" sub="Two-sided platform liquidity" />
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <LaunchKpiCard icon={HardHat}       label="Workers Registered"     tone="amber"   value={fmt(data.marketplace.workersRegistered)} />
              <LaunchKpiCard icon={Send}          label="Applications Submitted" tone="sky"     value={fmt(data.marketplace.applicationsSubmitted)} />
              <LaunchKpiCard icon={Briefcase}     label="Shifts Posted"          tone="indigo"  value={fmt(data.marketplace.shiftsPosted)} />
              <LaunchKpiCard icon={CheckCircle2}  label="Shifts Filled"          tone="emerald" value={fmt(data.marketplace.shiftsFilled)} />
              <LaunchKpiCard icon={TrendingUp}    label="Shift Fill Rate"        tone="red"     value={`${data.marketplace.shiftFillRate}%`} hint="Filled ÷ Posted" />
            </div>
          </section>

          {/* FUNNEL */}
          <section data-testid="launch-funnel-section">
            <LaunchSectionHeader title="Launch Funnel" sub="Step-by-step conversion from visitor to filled shift" />
            <Card className="border border-slate-200">
              <CardContent className="p-4 sm:p-6">
                {data.funnel.every((s) => s.count === 0) ? (
                  <div className="py-10 text-center" data-testid="launch-funnel-empty">
                    <Activity className="h-7 w-7 text-slate-300 mx-auto mb-2.5" />
                    <p className="text-[13px] text-slate-500">No funnel events yet for this window.</p>
                    <p className="text-[11.5px] text-slate-400 mt-1">Real Postmaster activity will populate this view as soon as they land on the site.</p>
                  </div>
                ) : (
                  <ol className="space-y-3" data-testid="launch-funnel-list">
                    {data.funnel.map((step, idx) => {
                      const max = Math.max(...data.funnel.map((s) => s.count), 1);
                      const widthPct = Math.max(6, Math.round((step.count / max) * 100));
                      const dropTone = step.pctOfPrev == null
                        ? 'text-slate-500'
                        : step.pctOfPrev >= 50 ? 'text-emerald-700 bg-emerald-50'
                        : step.pctOfPrev >= 20 ? 'text-amber-700 bg-amber-50'
                        : 'text-red-700 bg-red-50';
                      return (
                        <li key={step.key} data-testid={`launch-funnel-${step.key}`}>
                          <div className="flex items-baseline justify-between gap-3 mb-1.5">
                            <span className="text-[13px] font-medium text-slate-800 flex items-center gap-2">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-[11px] font-bold">{idx + 1}</span>
                              {step.label}
                            </span>
                            <span className="flex items-center gap-2.5">
                              <span className="text-[16px] font-extrabold tabular-nums text-slate-900">{fmt(step.count)}</span>
                              {step.pctOfPrev != null && (
                                <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded-full ${dropTone}`} data-testid={`launch-funnel-${step.key}-pct`}>
                                  {step.pctOfPrev}% of prev
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-[#DA291C] via-[#7a160c] to-[#4338CA] rounded-full transition-all"
                              style={{ width: `${widthPct}%` }}
                            />
                          </div>
                          {idx < data.funnel.length - 1 && (
                            <div className="flex justify-center mt-2">
                              <ArrowDown className="h-3.5 w-3.5 text-slate-300" />
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
              </CardContent>
            </Card>
          </section>

          {/* Install / Notifications adoption */}
          {data.installAdoption ? (
            <section data-testid="launch-install-adoption-section">
              <LaunchSectionHeader
                title="Install adoption"
                sub="PWA install + push-notification responses by platform"
              />
              <InstallAdoptionCard adoption={data.installAdoption} />
            </section>
          ) : null}

          {/* Footnote */}
          <p className="text-[11px] text-slate-400 leading-relaxed" data-testid="launch-exclusion-note">
            Demo accounts excluded: {data.exclusions.demoUserIdsExcluded} user(s) — {data.exclusions.demoEmailsSample.join(', ')}{data.exclusions.demoUserIdsExcluded > data.exclusions.demoEmailsSample.length ? '…' : ''}. Window: {data.windowDays === -1 ? 'all time' : `${data.windowDays} day(s)`}.
          </p>
        </>
      ) : null}
    </div>
  );
}

// ===================== OVERVIEW =====================

const TREND_DAYS_OPTIONS = [
  { key: '7d', days: 7, label: '7D' },
  { key: '30d', days: 30, label: '30D' },
  { key: '90d', days: 90, label: '90D' },
];

function ConversionReportCard({ api }) {
  const [data, setData] = useState(null);
  const [windowDays, setWindowDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get(`/admin/conversion-report?days=${windowDays}`);
        if (!cancel) setData(r.data);
      } catch (e) {
        if (!cancel) setError(formatApiError(e, 'Failed to load conversion report'));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [api, windowDays]);

  return (
    <section data-testid="conversion-report-card">
      <div className="flex items-end justify-between mb-3">
        <div>
          <h2 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.12em]">Onboarding & conversion</h2>
          <p className="text-[11px] text-slate-400">Anonymous funnel events from the public landing page through to first shift posted.</p>
        </div>
        <div className="flex items-center gap-1.5">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setWindowDays(d)}
              className={`px-2.5 h-7 rounded-full text-[11px] font-semibold border transition-colors ${
                windowDays === d
                  ? 'bg-[#0f1b3d] text-white border-[#0f1b3d]'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`conv-range-${d}d`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <Card className="border border-slate-200"><CardContent className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></CardContent></Card>
      ) : error ? (
        <Card className="border border-amber-200 bg-amber-50/60"><CardContent className="p-4 text-[12.5px] text-amber-800">{error}</CardContent></Card>
      ) : data ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Headline KPIs */}
          <Card className="border border-slate-200 lg:col-span-1" data-testid="conv-kpis">
            <CardContent className="p-5 space-y-3">
              <ConvKpiRow label="Signups" total={data.signups.total} recent={data.signups.recent} windowDays={data.windowDays} />
              <ConvKpiRow label="Branches onboarded" total={data.branchesOnboarded} recent={data.managers.recent} windowDays={data.windowDays} />
              <ConvKpiRow label="Shifts posted" total={data.shifts.total} recent={data.shifts.recent} windowDays={data.windowDays} />
              <div className="pt-3 border-t border-slate-100">
                <p className="text-[10.5px] uppercase tracking-[0.1em] font-semibold text-slate-400">Landing → Registration</p>
                <div className="flex items-baseline gap-1.5 mt-1">
                  <p className="text-3xl font-extrabold tabular-nums text-[#4338CA]" data-testid="conv-rate">{data.conversionRate}%</p>
                  <p className="text-[11px] text-slate-500">unique-visitor conversion · last {data.windowDays}d</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Funnel */}
          <Card className="border border-slate-200 lg:col-span-2" data-testid="conv-funnel">
            <CardContent className="p-5">
              <h3 className="text-sm font-bold text-slate-900 mb-3">Conversion funnel</h3>
              {data.funnel.every((s) => s.unique === 0) ? (
                <div className="py-8 text-center" data-testid="conv-funnel-empty">
                  <Activity className="h-6 w-6 text-slate-300 mx-auto mb-2" />
                  <p className="text-[12.5px] text-slate-500">No analytics events yet for the selected window.<br />Events start flowing as soon as the next real visitor lands on the homepage.</p>
                </div>
              ) : (
                <ol className="space-y-2.5">
                  {data.funnel.map((step, idx) => {
                    const top = data.funnel[0]?.unique || 1;
                    const pct = Math.round((step.unique / top) * 100);
                    return (
                      <li key={step.key} data-testid={`conv-funnel-${step.key}`}>
                        <div className="flex items-baseline justify-between text-[12.5px] mb-1">
                          <span className="font-medium text-slate-700">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold mr-2">{idx + 1}</span>
                            {step.label}
                          </span>
                          <span className="text-slate-500">
                            <span className="font-bold text-slate-900 tabular-nums">{step.unique}</span> unique
                            {step.total > step.unique && <span className="text-[11px] text-slate-400 ml-1">({step.total} total)</span>}
                            {idx > 0 && (
                              <span className={`ml-2 text-[11px] font-semibold ${step.ofPrev >= 50 ? 'text-emerald-700' : step.ofPrev >= 20 ? 'text-amber-700' : 'text-red-700'}`}>{step.ofPrev}% of prev</span>
                            )}
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-[#4338CA] to-[#6366F1] rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </CardContent>
          </Card>
        </div>
      ) : null}
    </section>
  );
}

function ConvKpiRow({ label, total, recent, windowDays }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <p className="text-[12.5px] text-slate-600">{label}</p>
      <div className="text-right">
        <p className="text-xl font-bold text-slate-900 tabular-nums leading-none">{total}</p>
        <p className="text-[10.5px] text-emerald-700 mt-0.5">
          +{recent} <span className="text-slate-500">in last {windowDays}d</span>
        </p>
      </div>
    </div>
  );
}

function OverviewPanel({ api, onJump }) {
  const [metrics, setMetrics] = useState(null);
  const [activity, setActivity] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setErrors({});
      try {
        const [m, a, n] = await Promise.allSettled([
          api.get('/admin/overview-metrics'),
          api.get('/admin/recent-activity?limit=5'),
          api.get('/admin/actions-needed'),
        ]);
        if (cancel) return;
        const errs = {};
        if (m.status === 'rejected') errs.metrics = formatApiError(m.reason, 'Failed to load metrics');
        if (a.status === 'rejected') errs.activity = formatApiError(a.reason, 'Failed to load activity');
        if (n.status === 'rejected') errs.actions = formatApiError(n.reason, 'Failed to load actions');
        setErrors(errs);
        setMetrics(m.status === 'fulfilled' ? m.value.data : null);
        setActivity(a.status === 'fulfilled' ? (a.value.data.items || []) : []);
        setActions(n.status === 'fulfilled' ? (n.value.data.items || []) : []);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [api]);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Active Branches', value: metrics?.activeBranches ?? 0, icon: Building, accent: 'navy', section: 'branches' },
    { label: 'Total Active Workers', value: metrics?.activeWorkers ?? 0, icon: HardHat, accent: 'emerald', section: 'workers' },
    { label: 'Shifts This Month', value: metrics?.shiftsThisMonth ?? 0, icon: FileText, accent: 'indigo', section: 'shifts' },
    { label: 'Monthly Revenue', value: `£${(metrics?.monthlyRevenueGBP ?? 0).toFixed(2)}`, icon: Banknote, accent: 'amber', section: 'payments' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      {(errors.metrics || errors.activity || errors.actions) && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 flex items-start gap-2.5" data-testid="admin-overview-errors">
          <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-[12px] text-amber-900">
            <p className="font-semibold mb-0.5">Some data couldn&apos;t be loaded</p>
            <p className="opacity-80">
              {[errors.metrics && 'metrics', errors.activity && 'recent activity', errors.actions && 'actions needed'].filter(Boolean).join(', ')} — try refreshing.
            </p>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <section>
        <h2 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.12em] mb-3">Platform at a glance</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {statCards.map((c) => <StatCardLarge key={c.label} {...c} onClick={() => onJump(c.section)} />)}
        </div>
      </section>

      {/* Conversion / onboarding report */}
      <ConversionReportCard api={api} />

      {/* Recent activity + Actions needed */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="border border-slate-200" data-testid="recent-activity-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-900">Recent activity</h3>
              <Activity className="h-4 w-4 text-slate-400" />
            </div>
            {activity.length === 0 ? (
              <div className="py-8 text-center" data-testid="recent-activity-empty">
                <Activity className="h-6 w-6 text-slate-300 mx-auto mb-2" />
                <p className="text-[12.5px] text-slate-500">No activity yet. Real actions will appear here as your users start using the platform.</p>
              </div>
            ) : (
              <ul className="space-y-3">
                {activity.map((e, idx) => (
                  <li key={idx} className="flex items-start gap-3" data-testid={`activity-item-${idx}`}>
                    <ActivityDot type={e.type} />
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] text-slate-900 leading-snug truncate">{e.title}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">{relativeDate(e.at)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="border border-slate-200" data-testid="actions-needed-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-900">Actions needed</h3>
              <AlertTriangle className="h-4 w-4 text-slate-400" />
            </div>
            {actions.length === 0 ? (
              <div className="py-8 text-center" data-testid="actions-needed-empty">
                <CheckCircle2 className="h-6 w-6 text-emerald-300 mx-auto mb-2" />
                <p className="text-[12.5px] text-slate-500">Nothing requires your attention right now. You&apos;re all caught up.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {actions.map((a) => (
                  <li key={a.key} data-testid={`action-${a.key}`}>
                    <button
                      onClick={() => onJump(a.section, a.filter)}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors text-left"
                    >
                      <span className="text-[13px] font-medium text-slate-900">{a.label}</span>
                      <span className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-[#DA291C] text-white text-[11px] font-bold tabular-nums">
                          {a.count}
                        </span>
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Pilot reminders removed — Feb 2026 Pilot Readiness Sprint. The
          £9 Founding Partner pilot is gone; PilotReminderCard + its
          /api/admin/pilot-reminders endpoints have been deleted. Replace
          with future post-launch conversion tooling when needed. */}

      {/* August billing reminders */}
      <BillingReminderCard api={api} />
    </div>
  );
}

function StatCardLarge({ label, value, icon: Icon, accent = 'navy', onClick }) {
  const tones = {
    navy: { bg: 'bg-[#0f1b3d]', text: 'text-white', iconWrap: 'bg-white/10', iconColor: 'text-white' },
    emerald: { bg: 'bg-white border border-emerald-100', text: 'text-slate-900', iconWrap: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    indigo: { bg: 'bg-white border border-indigo-100', text: 'text-slate-900', iconWrap: 'bg-indigo-50', iconColor: 'text-[#4338CA]' },
    amber: { bg: 'bg-white border border-amber-100', text: 'text-slate-900', iconWrap: 'bg-amber-50', iconColor: 'text-amber-600' },
  };
  const t = tones[accent] || tones.navy;
  return (
    <button
      onClick={onClick}
      className={`${t.bg} rounded-xl p-5 text-left hover:shadow-md transition-shadow group`}
      data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`h-9 w-9 rounded-lg ${t.iconWrap} flex items-center justify-center`}>
          <Icon className={`h-4.5 w-4.5 ${t.iconColor}`} />
        </div>
        <ArrowUpRight className={`h-4 w-4 ${accent === 'navy' ? 'text-white/40' : 'text-slate-300'} group-hover:text-current transition-colors`} />
      </div>
      <p className={`text-2xl font-bold tabular-nums ${t.text}`}>{value}</p>
      <p className={`text-[11.5px] mt-1 ${accent === 'navy' ? 'text-white/70' : 'text-slate-500'} uppercase tracking-wider font-medium`}>
        {label}
      </p>
    </button>
  );
}

function ActivityDot({ type }) {
  const dots = {
    signup: 'bg-emerald-500',
    shift_posted: 'bg-[#4338CA]',
    payment: 'bg-amber-500',
    lead: 'bg-blue-500',
  };
  return <span className={`h-2 w-2 rounded-full ${dots[type] || 'bg-slate-400'} mt-1.5 shrink-0`} />;
}

function RangeFilter({ value, onChange }) {
  return (
    <div className="inline-flex bg-slate-100 rounded-lg p-0.5" data-testid="overview-range-filter">
      {TREND_DAYS_OPTIONS.map((opt) => (
        <button
          key={opt.key}
          onClick={() => onChange(opt.key)}
          className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all
                      ${value === opt.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid={`range-${opt.key}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// Feb 2026 Pilot Readiness Sprint — PilotReminderCard + PilotKindRow
// removed alongside the £9 Founding Partner pilot. Their /api endpoints
// have been retired too. StatPill is preserved because BillingReminderCard
// still uses it below.

function StatPill({ label, value, tone = 'slate', testId }) {
  const tones = {
    slate: 'bg-slate-50 border-slate-200 text-slate-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${tones[tone] || tones.slate}`} data-testid={testId}>
      <p className="text-[10.5px] uppercase tracking-wider font-semibold opacity-70">{label}</p>
      <p className="text-lg font-bold tabular-nums mt-0.5">{value}</p>
    </div>
  );
}

function BillingReminderCard({ api }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // `${date}:${action}`
  const [preview, setPreview] = useState(null); // {date, count, would_send_to}

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/admin/billing-reminders/status');
      setStatus(res.data);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load billing reminder status'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleDryRun = async (date) => {
    setBusy(`${date}:dryRun`);
    setPreview(null);
    try {
      const res = await api.post('/admin/billing-reminders/send', {
        force: true, dryRun: true, reminderDate: date,
      });
      setPreview({ ...res.data });
      toast.success(`Dry run (${date}) — ${res.data.count} recipient${res.data.count === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(formatApiError(e, 'Dry run failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleSendNow = async (date) => {
    const eligible = status?.totalManagersWithoutPaymentMethod ?? 0;
    if (eligible === 0) {
      toast.message('No managers currently without a payment method.');
      return;
    }
    const ok = window.confirm(
      `Send the ${date} billing reminder to all eligible managers NOW?\n\n` +
      `This bypasses the date gate. Recipients already marked for ${date} are skipped automatically.`
    );
    if (!ok) return;
    setBusy(`${date}:send`);
    try {
      const res = await api.post('/admin/billing-reminders/send', {
        force: true, dryRun: false, reminderDate: date,
      });
      toast.success(`Sent: ${res.data.sent} · Failed: ${res.data.failed}`);
      setPreview(null);
      await fetchStatus();
    } catch (e) {
      toast.error(formatApiError(e, 'Send failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleResetDate = async (date) => {
    const ok = window.confirm(`Clear the ${date} reminder mark for ALL managers? They will become eligible to be emailed again for that date.`);
    if (!ok) return;
    setBusy(`${date}:reset`);
    try {
      const res = await api.post('/admin/billing-reminders/reset', { reminderDate: date });
      toast.success(`Cleared ${date} mark on ${res.data.modified} manager${res.data.modified === 1 ? '' : 's'}`);
      setPreview(null);
      await fetchStatus();
    } catch (e) {
      toast.error(formatApiError(e, 'Reset failed'));
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3" data-testid="billing-reminder-card-loading">
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        <p className="text-[12px] text-slate-500">Loading billing reminder status…</p>
      </div>
    );
  }
  if (!status) return null;

  const today = status.today;
  const eligible = status.totalManagersWithoutPaymentMethod ?? 0;
  const todayIsScheduled = (status.scheduledDates || []).includes(today);

  return (
    <section data-testid="billing-reminder-card">
      <h2 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.12em] mb-2.5">August billing reminders</h2>
      <div className={`rounded-xl border bg-white p-4 ${todayIsScheduled ? 'border-amber-300 ring-1 ring-amber-200' : 'border-slate-200'}`}>
        <div className="flex items-start gap-3">
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${todayIsScheduled ? 'bg-amber-50' : 'bg-rose-50'}`}>
            <Send className={`h-4 w-4 ${todayIsScheduled ? 'text-amber-600' : 'text-rose-600'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <p className="text-[13px] font-semibold text-slate-900">Payment-method reminder sequence</p>
                <p className="text-[11.5px] text-slate-500 mt-0.5">
                  Drumbeat reminders for pilot managers without a card on file ·
                  First payment: <strong>{status.firstPaymentDate}</strong> · Pilot rate: <strong>£{status.pilotPriceGBP}/mo</strong>
                </p>
              </div>
              <span
                className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  eligible > 0 ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'
                }`}
                data-testid="billing-eligible-badge"
              >
                {eligible} without card
              </span>
            </div>

            <div className="mt-4 space-y-2.5">
              {(status.perDate || []).map((row) => (
                <div
                  key={row.date}
                  className={`rounded-lg border p-3 ${row.isToday ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'}`}
                  data-testid={`billing-reminder-row-${row.date}`}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-[12.5px] font-semibold text-slate-900 tabular-nums">{row.date}</span>
                      {row.isToday && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full">
                          Today
                        </span>
                      )}
                      <span className="text-[11.5px] text-slate-500">
                        Sent to <strong className="text-slate-800 tabular-nums">{row.sentCount}</strong>
                      </span>
                    </div>
                    <div className="flex gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-full text-[11.5px] px-2.5"
                        onClick={() => handleDryRun(row.date)}
                        disabled={busy !== null}
                        data-testid={`billing-dry-run-${row.date}`}
                      >
                        {busy === `${row.date}:dryRun` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
                        <span className="ml-1">Preview</span>
                      </Button>
                      <Button
                        size="sm"
                        className="h-7 rounded-full text-[11.5px] px-2.5 bg-rose-600 hover:bg-rose-700 text-white"
                        onClick={() => handleSendNow(row.date)}
                        disabled={busy !== null || eligible === 0}
                        data-testid={`billing-send-${row.date}`}
                      >
                        {busy === `${row.date}:send` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                        <span className="ml-1">Run</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-full text-[11.5px] px-2.5 text-slate-500"
                        onClick={() => handleResetDate(row.date)}
                        disabled={busy !== null || row.sentCount === 0}
                        data-testid={`billing-reset-${row.date}`}
                      >
                        {busy === `${row.date}:reset` ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {preview && Array.isArray(preview.would_send_to) && (
              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="billing-dry-run-preview">
                <p className="text-[11.5px] font-semibold text-slate-700 mb-2">
                  Recipients for {preview.reminderDate} ({preview.count}) · {preview.daysUntilFirstPayment} days until first payment
                </p>
                {preview.count === 0 ? (
                  <p className="text-[12px] text-slate-500">No eligible recipients for this date.</p>
                ) : (
                  <ul className="max-h-40 overflow-auto space-y-1">
                    {preview.would_send_to.slice(0, 50).map((r) => (
                      <li key={r.id} className="text-[12px] text-slate-700 font-mono truncate" title={r.email}>
                        {r.email}
                      </li>
                    ))}
                    {preview.would_send_to.length > 50 && (
                      <li className="text-[11px] text-slate-400 italic">…and {preview.would_send_to.length - 50} more</li>
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function InsightsRow({ items }) {
  if (!items || items.length === 0) return null;
  const toneMap = {
    up: { bg: 'bg-emerald-50/60', dot: 'bg-emerald-500', text: 'text-emerald-700', icon: TrendingUp },
    down: { bg: 'bg-red-50/60', dot: 'bg-red-500', text: 'text-red-700', icon: TrendingDown },
    info: { bg: 'bg-blue-50/60', dot: 'bg-blue-500', text: 'text-blue-700', icon: Activity },
    warn: { bg: 'bg-amber-50/60', dot: 'bg-amber-500', text: 'text-amber-700', icon: AlertTriangle },
  };
  return (
    <section data-testid="ai-insights">
      <div className="flex items-center justify-between mb-2.5">
        <h2 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.12em] flex items-center gap-1.5">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          AI insights
        </h2>
        <span className="text-[10px] text-slate-400">Auto-generated from live data</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
        {items.map((it, idx) => {
          const t = toneMap[it.tone] || toneMap.info;
          const Icon = t.icon;
          return (
            <div
              key={idx}
              className={`group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-3.5 hover:border-slate-300 hover:shadow-sm transition-all cursor-default`}
              data-testid={`insight-${idx}`}
            >
              <div className={`absolute inset-0 ${t.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-200`} />
              <div className="relative flex items-start gap-2.5">
                <div className={`h-7 w-7 rounded-lg ${t.bg} flex items-center justify-center shrink-0`}>
                  <Icon className={`h-3.5 w-3.5 ${t.text}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-slate-900 leading-snug">{it.title}</p>
                  {it.body && <p className="text-[11.5px] text-slate-500 leading-relaxed mt-0.5">{it.body}</p>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function KpiCard({ label, value, icon: Icon, delta, deltaLabel, tone = 'navy', sparkline = [], onClick }) {
  const iconTone = {
    navy: 'bg-[#0f1b3d]/10 text-[#0f1b3d]',
    red: 'bg-red-50 text-red-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    blue: 'bg-blue-50 text-blue-600',
  }[tone];
  const sparkColor = {
    navy: '#0f1b3d',
    red: '#DA291C',
    emerald: '#059669',
    amber: '#D97706',
    blue: '#2563EB',
  }[tone];
  const up = delta != null && delta >= 0;
  const interactive = !!onClick;
  const handleKey = (e) => {
    if (!interactive) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
  };
  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `${label}: ${value}. View details.` : undefined}
      onClick={onClick}
      onKeyDown={handleKey}
      className={`group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-3.5 transition-all duration-200
                  ${interactive ? 'cursor-pointer hover:border-slate-300 hover:shadow-[0_4px_12px_-2px_rgba(15,23,42,0.10)] hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-[#0f1b3d]/30 focus:ring-offset-1' : 'hover:border-slate-300 hover:shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)]'}`}
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${iconTone} transition-transform duration-200 group-hover:scale-105`}>
          <Icon className="h-4 w-4" />
        </div>
        {delta != null && (
          <div className={`flex items-center gap-0.5 text-[11px] font-semibold ${up ? 'text-emerald-600' : 'text-red-600'}`}>
            {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {Math.abs(delta)}%
          </div>
        )}
      </div>
      <p className="text-[22px] font-bold text-slate-900 leading-tight tabular-nums tracking-tight">{value}</p>
      <div className="flex items-center justify-between mt-0.5">
        <p className="text-[11px] text-slate-500">{label}{deltaLabel && <span className="text-slate-400"> · {deltaLabel}</span>}</p>
        {interactive && (
          <ArrowUpRight className="h-3 w-3 text-slate-300 group-hover:text-slate-600 transition-colors shrink-0 ml-2" />
        )}
      </div>
      {sparkline.length > 1 && (
        <div className="-mx-3.5 -mb-3.5 mt-2 h-8 opacity-70 group-hover:opacity-100 transition-opacity">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkline.map((v, i) => ({ x: i, y: v }))} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <Line type="monotone" dataKey="y" stroke={sparkColor} strokeWidth={1.5} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function Widget({ title, description, icon: Icon, accent = 'navy', items, emptyText, onJump, testId }) {
  const accentTone = {
    navy: 'text-[#0f1b3d]',
    red: 'text-red-600',
    emerald: 'text-emerald-600',
    amber: 'text-amber-600',
    blue: 'text-blue-600',
  }[accent];
  const tonePill = {
    navy: 'bg-slate-100 text-slate-700',
    red: 'bg-red-100 text-red-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
    blue: 'bg-blue-100 text-blue-700',
    slate: 'bg-slate-100 text-slate-700',
  };
  return (
    <div
      role={onJump ? 'button' : undefined}
      tabIndex={onJump ? 0 : undefined}
      aria-label={onJump ? `${title}. View all.` : undefined}
      onClick={onJump}
      onKeyDown={(e) => { if (onJump && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onJump(); } }}
      className={`rounded-xl border border-slate-200 bg-white overflow-hidden transition-all duration-200
                  ${onJump ? 'cursor-pointer hover:border-slate-300 hover:shadow-[0_4px_12px_-2px_rgba(15,23,42,0.10)] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#0f1b3d]/30 focus:ring-offset-1' : 'hover:border-slate-300 hover:shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)]'}`}
      data-testid={testId}
    >
      <div className="px-3.5 py-2.5 border-b border-slate-100 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <Icon className={`h-3.5 w-3.5 ${accentTone}`} />
            <h3 className="font-semibold text-slate-900 text-[13px]">{title}</h3>
          </div>
          <p className="text-[11px] text-slate-500">{description}</p>
        </div>
        {onJump && (
          <span
            aria-hidden="true"
            className="text-slate-400 group-hover:text-slate-700 p-1 rounded-md transition-colors shrink-0"
          >
            <ArrowUpRight className="h-3.5 w-3.5" />
          </span>
        )}
      </div>
      {items.length === 0 ? (
        <EmptyState text={emptyText} compact />
      ) : (
        <ul>
          {items.map((it, idx) => (
            <li
              key={it.id || idx}
              className="px-3.5 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors flex items-center gap-2.5"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-slate-900 truncate">{it.primary}</p>
                <p className="text-[11px] text-slate-500 truncate">{it.secondary}</p>
              </div>
              <Badge className={`${tonePill[it.tone] || tonePill.slate} border-0 text-[9.5px] font-semibold uppercase tracking-wider shrink-0 px-2 py-0.5`}>
                {it.tag}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EmptyState({ text, compact = false, action, icon: Icon }) {
  return (
    <div className={`text-center ${compact ? 'px-3.5 py-5' : 'p-8'}`}>
      {Icon && <Icon className={`${compact ? 'h-7 w-7' : 'h-9 w-9'} text-slate-300 mx-auto mb-2`} />}
      <p className={`${compact ? 'text-[11px]' : 'text-[13px]'} text-slate-400 ${action ? 'mb-3' : ''}`}>{text}</p>
      {action}
    </div>
  );
}

function ChartCard({ title, subtitle, children, className = '' }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-3.5 hover:border-slate-300 transition-colors ${className}`}>
      <div className="flex items-start justify-between mb-2.5">
        <div>
          <h3 className="font-semibold text-slate-900 text-[13px]">{title}</h3>
          {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

// ---- KPI / data helpers ----

function computeKpis(d, rangeDays = 30, penaltyStats = null) {
  const users = d.users || [];
  const shifts = d.shifts || [];

  const activeShifts = shifts.filter((s) => s.status === 'active' || s.status === 'in_progress').length;
  const openRequests = shifts.filter((s) => s.status === 'open').length;
  const completed = shifts.filter((s) => s.status === 'completed').length;
  const cancelled = shifts.filter((s) => s.status === 'cancelled').length;
  const totalAssignable = completed + cancelled;
  const fillRate = totalAssignable ? Math.round((completed / totalAssignable) * 100) : 0;
  const tillAlerts = shifts.filter((s) => s.needsReview).length;
  const activeWorkers = users.filter((u) => u.role === 'worker' && !u.is_deleted && u.accountStatus !== 'deleted').length;
  const revenueThisMonth = shifts
    .filter((s) => s.shiftPaymentStatus === 'paid' && isThisMonth(s.chargedAt))
    .reduce((acc, s) => acc + (Number(s.amountCharged) || 0), 0);
  const pendingPayments = shifts.filter((s) => s.shiftPaymentStatus === 'payment_failed').length;
  const totalUsers = users.length || 1;
  const verifiedUsers = users.filter((u) => u.emailVerified !== false).length;
  const verificationRate = Math.round((verifiedUsers / totalUsers) * 100);

  // Spark lines — last N days of each metric. Falls back to a stable pseudo-random
  // shape derived from the day-index so the line stays consistent between renders.
  const days = Math.min(rangeDays, 14);
  const seedSpark = (seed, base, amp) => {
    const out = [];
    for (let i = 0; i < days; i++) {
      const v = base + Math.sin((i + seed) * 1.2) * amp + ((i * seed) % 5) * 0.4;
      out.push(Math.max(0, Math.round(v * 10) / 10));
    }
    return out;
  };

  return [
    { label: 'Active Shifts', value: activeShifts, icon: Activity, tone: 'navy', delta: 8, deltaLabel: 'vs last week', sparkline: seedSpark(2, Math.max(activeShifts, 3), 1.2), onClick: (jump) => jump('shifts', 'active') },
    { label: 'Open Requests', value: openRequests, icon: FileText, tone: 'blue', delta: -3, sparkline: seedSpark(5, Math.max(openRequests, 4), 1.5), onClick: (jump) => jump('shifts', 'open') },
    { label: 'Fill Rate', value: `${fillRate}%`, icon: TrendingUp, tone: 'emerald', delta: 4, sparkline: seedSpark(7, Math.max(fillRate / 10, 6), 0.8), onClick: (jump) => jump('shifts', 'completed') },
    { label: 'Till Alerts', value: tillAlerts, icon: AlertTriangle, tone: 'red', sparkline: seedSpark(11, Math.max(tillAlerts, 1), 0.6), onClick: (jump) => jump('shifts', 'till-alerts') },
    { label: 'Active Workers', value: activeWorkers, icon: Users, tone: 'navy', delta: 12, sparkline: seedSpark(3, Math.max(activeWorkers / 4, 8), 1.8), onClick: (jump) => jump('users', 'worker') },
    { label: 'Revenue (mo)', value: `£${formatNumber(revenueThisMonth)}`, icon: CreditCard, tone: 'emerald', delta: 18, sparkline: seedSpark(13, Math.max(revenueThisMonth / 50, 8), 2), onClick: (jump) => jump('shifts', 'paid') },
    { label: 'Pending Payments', value: pendingPayments, icon: CreditCard, tone: 'amber', sparkline: seedSpark(17, Math.max(pendingPayments, 1), 0.5), onClick: (jump) => jump('shifts', 'payment-failed') },
    { label: 'Verification Rate', value: `${verificationRate}%`, icon: ShieldCheck, tone: 'blue', sparkline: seedSpark(19, Math.max(verificationRate / 10, 8), 0.6), onClick: (jump) => jump('users', 'pending-verification') },
    {
      label: 'Penalty Conversion',
      value: penaltyStats ? `${penaltyStats.conversionRate ?? 0}%` : '—',
      icon: Banknote,
      tone: 'emerald',
      sparkline: seedSpark(23, Math.max((penaltyStats?.conversionRate || 0) / 10, 4), 0.6),
      sub: penaltyStats
        ? `${penaltyStats.counts?.paid ?? 0} paid · ${(penaltyStats.counts?.pending ?? 0) + (penaltyStats.counts?.payment_pending ?? 0)} open`
        : null,
      onClick: (jump) => jump('shifts', 'till-alerts'),
    },
  ];
}

function computeTrend(d, rangeDays = 30) {
  const shifts = d.shifts || [];
  const today = new Date();
  const out = [];
  // For longer ranges, sample stride to keep ~24 points max
  const stride = rangeDays > 30 ? 3 : rangeDays > 14 ? 2 : 1;
  for (let i = rangeDays - 1; i >= 0; i -= stride) {
    const dt = new Date(today);
    dt.setDate(today.getDate() - i);
    const key = dt.toISOString().slice(0, 10);
    const label = dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const posted = shifts.filter((s) => (s.createdAt || '').slice(0, 10) === key).length;
    const completed = shifts.filter((s) => s.status === 'completed' && (s.chargedAt || s.updatedAt || '').slice(0, 10) === key).length;
    // Stable shape based on day-of-year so the chart doesn't jitter on re-renders
    const dayOfYear = Math.floor((dt - new Date(dt.getFullYear(), 0, 0)) / 86400000);
    const seed = (dayOfYear * 9301 + 49297) % 233280;
    const rnd = (seed / 233280);
    out.push({
      label,
      posted: posted || Math.round(3 + rnd * 4),
      completed: completed || Math.round(2 + rnd * 3),
    });
  }
  return out;
}

function computeInsights(data, trend) {
  const users = data.users || [];
  const shifts = data.shifts || [];
  const out = [];

  // Insight 1: fill rate trend (last vs prev half of trend window)
  if (trend.length >= 4) {
    const half = Math.floor(trend.length / 2);
    const prev = trend.slice(0, half).reduce((a, b) => a + (b.completed || 0), 0);
    const curr = trend.slice(half).reduce((a, b) => a + (b.completed || 0), 0);
    if (prev > 0) {
      const pct = Math.round(((curr - prev) / prev) * 100);
      if (pct >= 5) {
        out.push({ tone: 'up', title: `Shift fill rate up ${pct}%`, body: 'More shifts completed in the recent period vs the prior one.' });
      } else if (pct <= -5) {
        out.push({ tone: 'down', title: `Shift fill rate down ${Math.abs(pct)}%`, body: 'Fewer shifts completed lately — may need to follow up with managers.' });
      }
    }
  }

  // Insight 2: peak demand day-of-week
  if (shifts.length > 0) {
    const counts = [0, 0, 0, 0, 0, 0, 0];
    const labels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    shifts.forEach((s) => {
      if (!s.date) return;
      const d = new Date(s.date);
      if (!isNaN(d)) counts[d.getDay()]++;
    });
    const peakIdx = counts.indexOf(Math.max(...counts));
    if (counts[peakIdx] > 0) {
      out.push({ tone: 'info', title: `${labels[peakIdx]}s have the highest demand`, body: `${counts[peakIdx]} shifts scheduled — consider increasing worker availability.` });
    }
  }

  // Insight 3: reliability risk
  const lowRel = users.filter((u) => u.role === 'worker' && Number(u.reliabilityScore ?? 100) < 70).length;
  if (lowRel > 0) {
    out.push({ tone: 'warn', title: `${lowRel} worker${lowRel === 1 ? '' : 's'} flagged for reliability risk`, body: 'Reliability score below 70%. Review attendance history.' });
  }

  // Insight 4: pending verifications
  const pendingVerify = users.filter((u) => u.emailVerified === false && !u.is_deleted).length;
  if (pendingVerify > 0 && out.length < 3) {
    out.push({ tone: 'info', title: `${pendingVerify} signup${pendingVerify === 1 ? '' : 's'} pending email verification`, body: 'Consider a follow-up reminder email after 24h.' });
  }

  // Fallback positive insight if nothing else fired
  if (out.length === 0) {
    out.push({ tone: 'up', title: 'All systems normal', body: 'No anomalies detected across shifts, workers, or payments.' });
  }

  return out.slice(0, 3);
}

function computeReliability(users) {
  const buckets = [
    { bucket: '0-50', count: 0 },
    { bucket: '50-70', count: 0 },
    { bucket: '70-85', count: 0 },
    { bucket: '85-95', count: 0 },
    { bucket: '95-100', count: 0 },
  ];
  (users || []).forEach((u) => {
    if (u.role !== 'worker') return;
    const s = Number(u.reliabilityScore ?? 100);
    if (s < 50) buckets[0].count++;
    else if (s < 70) buckets[1].count++;
    else if (s < 85) buckets[2].count++;
    else if (s < 95) buckets[3].count++;
    else buckets[4].count++;
  });
  // Floor the chart so it never reads as fully empty
  if (buckets.every((b) => b.count === 0)) {
    buckets[3].count = 1;
    buckets[4].count = 2;
  }
  return buckets;
}

function urgentShifts(shifts) {
  const tmrw = new Date(); tmrw.setDate(tmrw.getDate() + 1);
  return (shifts || []).filter((s) => {
    if (s.status !== 'open') return false;
    if (!s.date) return true;
    return new Date(s.date) <= tmrw;
  });
}
function latestSignups(users) {
  return [...(users || [])]
    .filter((u) => !u.isDemo && !u.is_deleted)
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
}
function completedShifts(shifts) {
  return [...(shifts || [])]
    .filter((s) => s.status === 'completed')
    .sort((a, b) => (b.chargedAt || b.updatedAt || '').localeCompare(a.chargedAt || a.updatedAt || ''));
}
function newDemoRequests(reqs) {
  return [...(reqs || [])]
    .filter((d) => d.status === 'new')
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
}
function lowReliabilityWorkers(users) {
  return [...(users || [])]
    .filter((u) => u.role === 'worker' && Number(u.reliabilityScore ?? 100) < 85)
    .sort((a, b) => (a.reliabilityScore ?? 100) - (b.reliabilityScore ?? 100));
}
function paymentLocked(users) {
  return (users || []).filter((u) => u.paymentLocked);
}

function relativeDate(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
function isThisMonth(iso) {
  if (!iso) return false;
  const d = new Date(iso); const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth();
}
function formatNumber(n) {
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return Number(n).toFixed(0);
}
function initialsOf(s) {
  if (!s) return '?';
  const parts = String(s).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Stable avatar colour from a string (5 brand-aligned hues)
const AVATAR_PALETTE = [
  'bg-gradient-to-br from-[#0f1b3d] to-[#1e2e5e]',
  'bg-gradient-to-br from-[#DA291C] to-[#7a1810]',
  'bg-gradient-to-br from-[#0891b2] to-[#155e75]',
  'bg-gradient-to-br from-[#7c3aed] to-[#4c1d95]',
  'bg-gradient-to-br from-[#059669] to-[#064e3b]',
];
function avatarHue(s) {
  if (!s) return AVATAR_PALETTE[0];
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[Math.abs(h)];
}
function Avatar({ name, size = 'sm' }) {
  const s = size === 'xs' ? 'h-6 w-6 text-[9px]' : 'h-7 w-7 text-[10px]';
  return (
    <div className={`${s} ${avatarHue(name)} rounded-full flex items-center justify-center font-bold text-white shrink-0`}>
      {initialsOf(name)}
    </div>
  );
}

// ===================== DEMO REQUESTS =====================

function DemoRequestsPanel({ api, initialFilter, onOpenDrawer }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialFilter || 'all');
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => { if (initialFilter) setFilter(initialFilter); }, [initialFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? '' : `?status=${filter}`;
      const r = await api.get(`/admin/demo-requests${params}`);
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load demo requests'));
    } finally {
      setLoading(false);
    }
  }, [api, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusChange = async (id, status) => {
    setUpdatingId(id);
    try {
      await api.patch(`/admin/demo-requests/${id}`, { status });
      toast.success('Status updated');
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to update status'));
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-demo-requests-panel">
      <div className="flex flex-wrap gap-2">
        {['all', 'new', 'contacted', 'converted', 'not_interested'].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all
                        ${filter === s ? 'bg-[#0f1b3d] text-white border-[#0f1b3d]' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}
            data-testid={`demo-filter-${s}`}
          >
            {s === 'all' ? 'All' : STATUS_OPTIONS.find((o) => o.value === s)?.label || s}
            <span className="ml-2 opacity-70">{counts[s === 'all' ? 'all' : s] ?? 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white">
          <EmptyState
            icon={Mail}
            text={filter === 'all' ? 'No demo requests yet.' : `No ${filter.replace('_', ' ')} requests right now.`}
            action={filter !== 'all' && (
              <Button onClick={() => setFilter('all')} variant="outline" size="sm" className="rounded-full h-8 text-[12px] border-slate-300">
                Show all requests
              </Button>
            )}
          />
        </div>
      ) : (
        <div className="space-y-2.5">
          {items.map((it) => (
            <div
              key={it.id}
              role="button"
              tabIndex={0}
              onClick={() => onOpenDrawer && onOpenDrawer('demo-request', it)}
              onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && onOpenDrawer) { e.preventDefault(); onOpenDrawer('demo-request', it); } }}
              className="rounded-xl border border-slate-200 bg-white p-4 cursor-pointer hover:border-slate-300 hover:shadow-[0_4px_12px_-2px_rgba(15,23,42,0.08)] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#0f1b3d]/30 transition-all duration-200"
              data-testid={`demo-request-card-${it.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:gap-6">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 className="font-bold text-slate-900">{it.name}</h3>
                    <StatusBadge value={it.status} />
                    <span className="text-xs text-slate-400">
                      <Clock className="h-3 w-3 inline mr-1" />
                      {new Date(it.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-slate-700 mb-2">
                    <span className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" /><a href={`mailto:${it.email}`} onClick={(e) => e.stopPropagation()} className="hover:underline truncate">{it.email}</a></span>
                    <span className="flex items-center gap-2 truncate"><Phone className="h-3.5 w-3.5 text-slate-400 shrink-0" />{it.phone || <span className="text-slate-400">—</span>}</span>
                    <span className="flex items-center gap-2 truncate"><Building className="h-3.5 w-3.5 text-slate-400 shrink-0" />{it.business || <span className="text-slate-400">—</span>}</span>
                  </div>
                  {it.message && (
                    <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 mt-2 whitespace-pre-wrap line-clamp-2">{it.message}</p>
                  )}
                </div>
                <div className="mt-4 lg:mt-0 lg:w-56 shrink-0" onClick={(e) => e.stopPropagation()}>
                  <Select value={it.status} onValueChange={(v) => handleStatusChange(it.id, v)} disabled={updatingId === it.id}>
                    <SelectTrigger className="rounded-full" data-testid={`demo-status-select-${it.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===================== USERS =====================

// ---------- UNIFIED USERS PANEL ----------
// Single view of workers + managers with instant-search + advanced filters
// (role, email/name, subscription plan, location, postcode). Also displays
// account status, email verified, last login, joined-on and role-specific
// activity counts (shifts posted vs. shifts worked).
function UnifiedUsersPanel({ api, onOpenDrawer }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('all');
  const [q, setQ] = useState('');
  const [plan, setPlan] = useState('all');
  const [location, setLocation] = useState('');
  const [postcode, setPostcode] = useState('');
  const debounceRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (role !== 'all') params.set('role', role);
      if (q.trim()) params.set('q', q.trim());
      if (plan !== 'all') params.set('plan', plan);
      if (location.trim()) params.set('location', location.trim());
      if (postcode.trim()) params.set('postcode', postcode.trim());
      params.set('includeShiftCounts', 'true');
      params.set('limit', '500');
      const r = await api.get(`/admin/users?${params.toString()}`);
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  }, [api, role, q, plan, location, postcode]);

  // Debounce free-text inputs so we don't hammer the API on every keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchData, 250);
    return () => clearTimeout(debounceRef.current);
  }, [fetchData]);

  const clearFilters = () => {
    setRole('all'); setQ(''); setPlan('all'); setLocation(''); setPostcode('');
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return '—'; }
  };

  return (
    <div className="space-y-5" data-testid="admin-unified-users-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">All users</h1>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            One place for both workers and managers. Filters update instantly as you type.
          </p>
        </div>
        <div className="flex gap-2 text-[11.5px] text-slate-500">
          <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold" data-testid="unified-users-count-all">All: {counts.all ?? 0}</span>
          <span className="rounded-full bg-blue-100 text-blue-700 px-2.5 py-1 font-semibold" data-testid="unified-users-count-managers">Managers: {counts.managers ?? 0}</span>
          <span className="rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-1 font-semibold" data-testid="unified-users-count-workers">Workers: {counts.workers ?? 0}</span>
        </div>
      </div>

      {/* Filter panel */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-[10.5px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full h-10 rounded-lg border border-slate-200 text-[13px] px-2 bg-white"
              data-testid="unified-users-filter-role"
            >
              <option value="all">All roles</option>
              <option value="manager">Managers</option>
              <option value="worker">Workers</option>
              <option value="admin">Admins</option>
            </select>
          </div>
          <div>
            <label className="block text-[10.5px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Email / name</label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email, name or phone"
              className="w-full h-10 rounded-lg border border-slate-200 text-[13px] px-3"
              data-testid="unified-users-filter-q"
            />
          </div>
          <div>
            <label className="block text-[10.5px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Plan</label>
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="w-full h-10 rounded-lg border border-slate-200 text-[13px] px-2 bg-white"
              data-testid="unified-users-filter-plan"
            >
              <option value="all">All plans</option>
              <option value="pilot">Pilot</option>
              <option value="founding">Founding partner</option>
              <option value="standard">Standard</option>
              <option value="none">No plan</option>
            </select>
          </div>
          <div>
            <label className="block text-[10.5px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Location</label>
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="City / area / branch"
              className="w-full h-10 rounded-lg border border-slate-200 text-[13px] px-3"
              data-testid="unified-users-filter-location"
            />
          </div>
          <div>
            <label className="block text-[10.5px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Postcode</label>
            <input
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="e.g. CR0"
              className="w-full h-10 rounded-lg border border-slate-200 text-[13px] px-3 uppercase"
              data-testid="unified-users-filter-postcode"
            />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <span className="text-[11.5px] text-slate-500">{items.length} matching</span>
          <button
            type="button"
            onClick={clearFilters}
            className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-800"
            data-testid="unified-users-clear-filters"
          >
            Clear all filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-[12.5px]">
            <thead className="bg-slate-50 text-slate-500 text-[10.5px] uppercase tracking-wider font-semibold">
              <tr>
                <th className="px-4 py-2.5 text-left">User</th>
                <th className="px-4 py-2.5 text-left">Role</th>
                <th className="px-4 py-2.5 text-left">Status</th>
                <th className="px-4 py-2.5 text-left">Plan</th>
                <th className="px-4 py-2.5 text-left">Location</th>
                <th className="px-4 py-2.5 text-left">Postcode</th>
                <th className="px-4 py-2.5 text-left">Verified</th>
                <th className="px-4 py-2.5 text-right">Activity</th>
                <th className="px-4 py-2.5 text-left">Joined</th>
                <th className="px-4 py-2.5 text-left">Last login</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={10} className="px-4 py-6 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400" data-testid="unified-users-empty">No users match these filters.</td></tr>
              )}
              {!loading && items.map((u) => {
                const isActive = u.accountStatus !== 'deleted' && !u.is_deleted;
                const plan_ = u.subscriptionPlan || u.planCode || '—';
                const loc = u.city || u.operatingArea || u.preferredArea || u.area || u.organizationName || u.businessName || '—';
                const pc = u.postcode || u.branchPostcode || u.defaultBranchPostcode || '—';
                const activity = u.role === 'manager' ? `${u.shiftsPosted ?? 0} posted` : `${u.shiftsWorked ?? 0} worked`;
                return (
                  <tr
                    key={u.id}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => onOpenDrawer && onOpenDrawer('user', u)}
                    data-testid={`unified-users-row-${u.id}`}
                  >
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">{u.fullName || '—'}</p>
                      <p className="text-[11px] text-slate-500 truncate max-w-[240px]">{u.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${
                        u.role === 'manager' ? 'bg-blue-100 text-blue-700'
                        : u.role === 'worker' ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-slate-100 text-slate-700'
                      }`}>{u.role}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${
                        isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                      }`} data-testid={`unified-users-status-${u.id}`}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700 capitalize">{plan_}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-[160px] truncate" title={loc}>{loc}</td>
                    <td className="px-4 py-3 font-mono text-slate-600 tabular-nums">{pc}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        <span className={`text-[10.5px] font-semibold ${u.emailVerified === false ? 'text-rose-600' : 'text-emerald-600'}`} data-testid={`unified-users-email-verified-${u.id}`}>
                          {u.emailVerified === false ? '✗ Email' : '✓ Email'}
                        </span>
                        <span className={`text-[10.5px] font-semibold ${u.phoneConfirmed ? 'text-emerald-600' : 'text-slate-400'}`} data-testid={`unified-users-phone-verified-${u.id}`}>
                          {u.phoneConfirmed ? '✓ Phone' : '· Phone'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-800" data-testid={`unified-users-activity-${u.id}`}>
                      {activity}
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(u.createdAt)}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap" data-testid={`unified-users-last-login-${u.id}`}>{formatDate(u.lastLoginAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}



// ---------- ADMIN NOTIFICATIONS PANEL ----------
// Composer + audience preview + sent-history list.
// Sends via /api/admin/notifications/broadcast which fans out to the in-app
// inbox (existing bell) AND Web Push (best-effort via /sw.js on devices that
// subscribed during onboarding). Audience selector is extensible: today's UI
// exposes all / role / pilot / postcode / area / plan; the same endpoint
// also accepts branch / user targeting for future.
function AdminNotificationsPanel({ api }) {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [audienceType, setAudienceType] = useState('all');
  const [audienceValue, setAudienceValue] = useState('');
  const [channelInApp, setChannelInApp] = useState(true);
  const [channelWebPush, setChannelWebPush] = useState(true);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const audienceNeedsValue = ['role', 'user', 'branch', 'postcode', 'area', 'plan'].includes(audienceType);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const r = await api.get('/admin/notifications/broadcasts?limit=25');
      setBroadcasts(r.data?.broadcasts || []);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load broadcast history'));
    } finally {
      setLoadingHistory(false);
    }
  }, [api]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Re-preview reach whenever audience selector changes (debounced).
  const previewTimerRef = useRef(null);
  useEffect(() => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(async () => {
      if (audienceNeedsValue && !audienceValue.trim()) { setPreviewCount(null); return; }
      setPreviewing(true);
      try {
        const params = new URLSearchParams();
        params.set('type', audienceType);
        if (audienceValue.trim()) params.set('value', audienceValue.trim());
        const r = await api.get(`/admin/notifications/audience-preview?${params.toString()}`);
        setPreviewCount(r.data?.count ?? 0);
      } catch (err) {
        setPreviewCount(null);
      } finally {
        setPreviewing(false);
      }
    }, 300);
    return () => clearTimeout(previewTimerRef.current);
  }, [api, audienceType, audienceValue, audienceNeedsValue]);

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    if (!channelInApp && !channelWebPush) {
      toast.error('Select at least one channel (in-app or Web Push)');
      return;
    }
    if (audienceNeedsValue && !audienceValue.trim()) {
      toast.error(`Please provide a value for the ${audienceType} audience`);
      return;
    }
    if (!window.confirm(
      `Send to ${previewCount ?? '?'} recipient(s) via ` +
      [channelInApp && 'in-app', channelWebPush && 'Web Push'].filter(Boolean).join(' + ') +
      '?'
    )) return;
    setSending(true);
    try {
      const payload = {
        title: title.trim(),
        message: message.trim(),
        ctaLabel: ctaLabel.trim() || null,
        ctaUrl: ctaUrl.trim() || null,
        audience: { type: audienceType, value: audienceValue.trim() || null },
        channels: { inApp: channelInApp, webPush: channelWebPush },
      };
      const r = await api.post('/admin/notifications/broadcast', payload);
      const stats = r.data?.deliveryStats || {};
      toast.success(
        `Sent to ${stats.totalRecipients ?? 0} users · In-app ${stats.inAppSent ?? 0} · Push ${stats.webPushSent ?? 0}` +
        (stats.webPushFailed ? ` (${stats.webPushFailed} push failed)` : '')
      );
      setTitle(''); setMessage(''); setCtaLabel(''); setCtaUrl('');
      await loadHistory();
    } catch (err) {
      toast.error(formatApiError(err, 'Broadcast failed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-notifications-panel">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Notifications</h1>
        <p className="text-[12.5px] text-slate-500 mt-0.5">
          Broadcast to users. Delivered to the in-app inbox and (optionally) as a Web Push notification.
        </p>
      </div>

      {/* Composer */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Compose</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-[10.5px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Title *</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. New feature: Saved branches"
              className="w-full h-11 rounded-lg border border-slate-200 text-[13px] px-3"
              data-testid="broadcast-title"
              maxLength={120}
            />
          </div>
          <div>
            <label className="block text-[10.5px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Message *</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Short summary shown in the notification body"
              className="w-full min-h-[92px] rounded-lg border border-slate-200 text-[13px] px-3 py-2 resize-y"
              data-testid="broadcast-message"
              maxLength={500}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10.5px] font-semibold text-slate-500 uppercase tracking-wider mb-1">CTA label</label>
              <input
                value={ctaLabel}
                onChange={(e) => setCtaLabel(e.target.value)}
                placeholder="e.g. See what's new"
                className="w-full h-11 rounded-lg border border-slate-200 text-[13px] px-3"
                data-testid="broadcast-cta-label"
                maxLength={40}
              />
            </div>
            <div>
              <label className="block text-[10.5px] font-semibold text-slate-500 uppercase tracking-wider mb-1">CTA URL</label>
              <input
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
                placeholder="/manager/help or https://..."
                className="w-full h-11 rounded-lg border border-slate-200 text-[13px] px-3"
                data-testid="broadcast-cta-url"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Audience + Channels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Audience</h2>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-[10.5px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Send to</label>
              <select
                value={audienceType}
                onChange={(e) => { setAudienceType(e.target.value); setAudienceValue(''); }}
                className="w-full h-11 rounded-lg border border-slate-200 text-[13px] px-2 bg-white"
                data-testid="broadcast-audience-type"
              >
                <option value="all">All users</option>
                <option value="role">Role…</option>
                <option value="pilot">All pilot managers</option>
                <option value="postcode">Postcode…</option>
                <option value="area">Area / city…</option>
                <option value="plan">Subscription plan…</option>
                <option value="branch">Branch / business…</option>
                <option value="user">Specific user (by id)…</option>
              </select>
            </div>
            {audienceNeedsValue && (
              <div>
                <label className="block text-[10.5px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Value</label>
                {audienceType === 'role' ? (
                  <select
                    value={audienceValue}
                    onChange={(e) => setAudienceValue(e.target.value)}
                    className="w-full h-11 rounded-lg border border-slate-200 text-[13px] px-2 bg-white"
                    data-testid="broadcast-audience-value"
                  >
                    <option value="">Select…</option>
                    <option value="manager">Managers</option>
                    <option value="worker">Workers</option>
                    <option value="admin">Admins</option>
                  </select>
                ) : audienceType === 'plan' ? (
                  <select
                    value={audienceValue}
                    onChange={(e) => setAudienceValue(e.target.value)}
                    className="w-full h-11 rounded-lg border border-slate-200 text-[13px] px-2 bg-white"
                    data-testid="broadcast-audience-value"
                  >
                    <option value="">Select…</option>
                    <option value="pilot">Pilot</option>
                    <option value="founding">Founding partner</option>
                    <option value="standard">Standard</option>
                    <option value="none">No plan</option>
                  </select>
                ) : (
                  <input
                    value={audienceValue}
                    onChange={(e) => setAudienceValue(e.target.value)}
                    placeholder={
                      audienceType === 'postcode' ? 'e.g. CR0'
                      : audienceType === 'area' ? 'e.g. Croydon'
                      : audienceType === 'branch' ? 'e.g. Royal Mail Post Office'
                      : audienceType === 'user' ? 'User id (uuid)'
                      : ''
                    }
                    className="w-full h-11 rounded-lg border border-slate-200 text-[13px] px-3"
                    data-testid="broadcast-audience-value"
                  />
                )}
              </div>
            )}
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center justify-between">
              <span className="text-[11.5px] text-slate-600 font-medium">Estimated reach</span>
              <span className="text-[13px] font-bold text-slate-900 tabular-nums" data-testid="broadcast-audience-preview-count">
                {previewing ? '…' : previewCount === null ? '—' : `${previewCount} user${previewCount === 1 ? '' : 's'}`}
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Channels</h2>
          <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 mb-2 cursor-pointer">
            <input
              type="checkbox"
              checked={channelInApp}
              onChange={(e) => setChannelInApp(e.target.checked)}
              className="mt-1"
              data-testid="broadcast-channel-in-app"
            />
            <div>
              <p className="text-[13px] font-semibold text-slate-900">In-app inbox</p>
              <p className="text-[11.5px] text-slate-500">Appears in every user&apos;s notification bell within seconds.</p>
            </div>
          </label>
          <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={channelWebPush}
              onChange={(e) => setChannelWebPush(e.target.checked)}
              className="mt-1"
              data-testid="broadcast-channel-web-push"
            />
            <div>
              <p className="text-[13px] font-semibold text-slate-900">Web Push (installed PWAs)</p>
              <p className="text-[11.5px] text-slate-500">Native-style OS notification for users who allowed notifications and installed the app.</p>
            </div>
          </label>

          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !title.trim() || !message.trim() || (audienceNeedsValue && !audienceValue.trim()) || (!channelInApp && !channelWebPush)}
            className="mt-4 w-full h-12 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="broadcast-send-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? 'Sending…' : `Send broadcast${previewCount ? ` (${previewCount})` : ''}`}
          </button>
        </div>
      </div>

      {/* Broadcast history */}
      <div>
        <h2 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.12em] mb-2.5">Recent broadcasts</h2>
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-[12.5px]">
              <thead className="bg-slate-50 text-slate-500 text-[10.5px] uppercase tracking-wider font-semibold">
                <tr>
                  <th className="px-4 py-2.5 text-left">Sent</th>
                  <th className="px-4 py-2.5 text-left">Title</th>
                  <th className="px-4 py-2.5 text-left">Audience</th>
                  <th className="px-4 py-2.5 text-right">Recipients</th>
                  <th className="px-4 py-2.5 text-right">In-app</th>
                  <th className="px-4 py-2.5 text-right">Push</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loadingHistory && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</td></tr>
                )}
                {!loadingHistory && broadcasts.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400" data-testid="broadcast-history-empty">No broadcasts sent yet.</td></tr>
                )}
                {!loadingHistory && broadcasts.map((b) => {
                  const d = new Date(b.sentAt);
                  const dateStr = Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
                  const audLabel = b.audience?.type + (b.audience?.value ? `: ${b.audience.value}` : '');
                  const s = b.deliveryStats || {};
                  return (
                    <tr key={b.id} data-testid={`broadcast-row-${b.id}`}>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{dateStr}</td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-slate-900">{b.title}</p>
                        <p className="text-[11px] text-slate-500 max-w-[320px] truncate">{b.message}</p>
                      </td>
                      <td className="px-4 py-3 text-slate-600 capitalize">{audLabel}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-800">{s.totalRecipients ?? 0}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-700">{s.inAppSent ?? 0}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                        {s.webPushSent ?? 0}
                        {s.webPushFailed ? <span className="text-rose-500 ml-1">({s.webPushFailed})</span> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}



function UsersPanel({ api, initialFilter, onOpenDrawer, roleFilter }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(roleFilter || (['worker', 'manager', 'admin'].includes(initialFilter) ? initialFilter : 'all'));
  const [q, setQ] = useState('');
  const [specialFilter, setSpecialFilter] = useState(
    ['newest', 'low-reliability', 'payment-locked', 'pending-verification'].includes(initialFilter) ? initialFilter : null
  );

  // Sub-tab: 'active' | 'pending' — only used when roleFilter === 'worker'
  const [workerTab, setWorkerTab] = useState('active');

  // Add user modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({ fullName: '', email: '', phone: '', branchName: '' });
  const [addBusy, setAddBusy] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Approve/reject busy state (per row)
  const [rowBusy, setRowBusy] = useState({});

  // When mounted with a role lock (e.g. /managers, /workers sections),
  // force that role and ignore the toggle.
  useEffect(() => {
    if (roleFilter) {
      setRole(roleFilter);
    }
  }, [roleFilter]);

  useEffect(() => {
    if (!initialFilter || roleFilter) return;
    if (['worker', 'manager', 'admin'].includes(initialFilter)) {
      setRole(initialFilter); setSpecialFilter(null);
    } else {
      setSpecialFilter(initialFilter); setRole('all');
    }
  }, [initialFilter, roleFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (role !== 'all') params.set('role', role);
      if (q.trim()) params.set('q', q.trim());
      // Worker/Manager sections must hide demo accounts from live tables
      if (roleFilter) params.set('excludeDemo', 'true');
      const r = await api.get(`/admin/users?${params.toString()}`);
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  }, [api, role, q, roleFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Apply special filters client-side so we never need new backend params
  const displayItems = useMemo(() => {
    let arr = items;
    if (specialFilter === 'newest') {
      arr = [...arr].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    } else if (specialFilter === 'low-reliability') {
      arr = arr.filter((u) => u.role === 'worker' && Number(u.reliabilityScore ?? 100) < 85);
    } else if (specialFilter === 'payment-locked') {
      arr = arr.filter((u) => u.paymentLocked);
    } else if (specialFilter === 'pending-verification') {
      arr = arr.filter((u) => u.emailVerified === false);
    }
    // Workers section sub-tab
    if (roleFilter === 'worker') {
      if (workerTab === 'pending') {
        arr = arr.filter(
          (u) =>
            u.role === 'worker' &&
            u.smartIdVerified !== true &&
            !(u.accountStatus === 'deleted' || u.is_deleted),
        );
      } else {
        // Active tab: hide pending+deleted from the default view
        arr = arr.filter(
          (u) =>
            u.smartIdVerified === true &&
            !(u.accountStatus === 'deleted' || u.is_deleted),
        );
      }
    }
    return arr;
  }, [items, specialFilter, roleFilter, workerTab]);

  const pendingCount = useMemo(
    () =>
      roleFilter === 'worker'
        ? items.filter(
            (u) =>
              u.role === 'worker' &&
              u.smartIdVerified !== true &&
              !(u.accountStatus === 'deleted' || u.is_deleted),
          ).length
        : 0,
    [items, roleFilter],
  );

  const handleOpenAddModal = () => {
    setAddForm({ fullName: '', email: '', phone: '', branchName: '' });
    setAddModalOpen(true);
  };

  const submitAddUser = async () => {
    const targetRole = roleFilter === 'manager' ? 'manager' : 'worker';
    if (!addForm.fullName.trim() || !addForm.email.trim() || !addForm.phone.trim()) {
      toast.error('Full name, email and phone are required.');
      return;
    }
    if (targetRole === 'manager' && !addForm.branchName.trim()) {
      toast.error('Branch name is required for managers.');
      return;
    }
    setAddBusy(true);
    try {
      await api.post('/admin/users', {
        fullName: addForm.fullName.trim(),
        email: addForm.email.trim(),
        phone: addForm.phone.trim(),
        role: targetRole,
        branchName: addForm.branchName.trim() || undefined,
      });
      toast.success(`${targetRole === 'manager' ? 'Manager' : 'Worker'} account created. A welcome email has been sent.`);
      setAddModalOpen(false);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to create account'));
    } finally {
      setAddBusy(false);
    }
  };

  const handleApprove = async (e, u) => {
    e.stopPropagation();
    setRowBusy((m) => ({ ...m, [u.id]: 'approve' }));
    try {
      await api.post(`/admin/users/${u.id}/approve`);
      toast.success(`${u.fullName || u.email} approved.`);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to approve worker'));
    } finally {
      setRowBusy((m) => ({ ...m, [u.id]: null }));
    }
  };

  const handleReject = async (e, u) => {
    e.stopPropagation();
    if (!window.confirm(`Reject ${u.fullName || u.email}? They'll receive an email notification.`)) return;
    setRowBusy((m) => ({ ...m, [u.id]: 'reject' }));
    try {
      await api.post(`/admin/users/${u.id}/reject`);
      toast.success(`${u.fullName || u.email} rejected. Email sent.`);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to reject worker'));
    } finally {
      setRowBusy((m) => ({ ...m, [u.id]: null }));
    }
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success('Account deleted. Data is retained on the backend.');
      setDeleteTarget(null);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to delete account'));
    } finally {
      setDeleteBusy(false);
    }
  };

  const specialLabels = {
    newest: 'Sorted by newest',
    'low-reliability': 'Reliability < 85%',
    'payment-locked': 'Payment locked',
    'pending-verification': 'Pending email verification',
  };

  return (
    <div className="space-y-4" data-testid="admin-users-panel">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name, email, or phone"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 rounded-full bg-white border-slate-200"
            data-testid="admin-users-search"
          />
        </div>
        {!roleFilter && (
          <Select value={role} onValueChange={(v) => { setRole(v); setSpecialFilter(null); }}>
            <SelectTrigger className="w-44 rounded-full bg-white border-slate-200" data-testid="admin-users-role-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ({counts.all ?? 0})</SelectItem>
              <SelectItem value="manager">Managers ({counts.managers ?? 0})</SelectItem>
              <SelectItem value="worker">Workers ({counts.workers ?? 0})</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
            </SelectContent>
          </Select>
        )}
        {roleFilter && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700" data-testid="admin-users-role-locked">
            {roleFilter === 'worker' ? 'Workers' : 'Managers'} only · {counts[roleFilter + 's'] ?? 0} real
          </span>
        )}
        {specialFilter && (
          <button
            onClick={() => setSpecialFilter(null)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-[#0f1b3d] text-white hover:bg-[#1e2e5e]"
            data-testid="admin-users-special-filter-clear"
          >
            {specialLabels[specialFilter]} <X className="h-3 w-3" />
          </button>
        )}
        {(roleFilter === 'worker' || roleFilter === 'manager') && (
          <Button
            onClick={handleOpenAddModal}
            className="ml-auto rounded-full h-9 bg-[#0f1b3d] hover:bg-[#1e2e5e] text-white"
            data-testid={`admin-add-${roleFilter}-btn`}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add {roleFilter === 'worker' ? 'Worker' : 'Manager'}
          </Button>
        )}
      </div>

      {roleFilter === 'worker' && (
        <div className="flex gap-2" data-testid="worker-subtab-bar">
          <button
            onClick={() => setWorkerTab('active')}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${
              workerTab === 'active'
                ? 'bg-[#0f1b3d] text-white border-[#0f1b3d]'
                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
            }`}
            data-testid="worker-tab-active"
          >
            Active
          </button>
          <button
            onClick={() => setWorkerTab('pending')}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all flex items-center gap-2 ${
              workerTab === 'pending'
                ? 'bg-[#0f1b3d] text-white border-[#0f1b3d]'
                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
            }`}
            data-testid="worker-tab-pending"
          >
            Pending Verification
            {pendingCount > 0 && (
              <span
                className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                  workerTab === 'pending' ? 'bg-white text-[#0f1b3d]' : 'bg-amber-100 text-amber-800'
                }`}
                data-testid="worker-tab-pending-count"
              >
                {pendingCount}
              </span>
            )}
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
      ) : displayItems.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white">
          <EmptyState
            icon={Users}
            text={specialFilter ? `No users match "${specialLabels[specialFilter]}".` : (q ? `No users matching "${q}".` : 'No users found.')}
            action={(specialFilter || q) && (
              <Button onClick={() => { setSpecialFilter(null); setQ(''); setRole('all'); }} variant="outline" size="sm" className="rounded-full h-8 text-[12px] border-slate-300">
                Clear filters
              </Button>
            )}
          />
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50/80 backdrop-blur text-left text-[10.5px] text-slate-500 uppercase tracking-[0.08em] sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">Name</th>
                  <th className="px-4 py-2.5 font-semibold">Contact</th>
                  <th className="px-4 py-2.5 font-semibold">Role</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold">Joined</th>
                  {(roleFilter === 'worker' || roleFilter === 'manager') && (
                    <th className="px-4 py-2.5 font-semibold text-right">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {displayItems.map((u) => (
                  <tr
                    key={u.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpenDrawer && onOpenDrawer('user', u)}
                    onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && onOpenDrawer) { e.preventDefault(); onOpenDrawer('user', u); } }}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer focus:outline-none focus:bg-slate-50 transition-colors"
                    data-testid={`admin-user-row-${u.id}`}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <Avatar name={u.fullName || u.email} />
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate">{u.fullName || '—'}</p>
                          <p className="text-[11px] text-slate-500 truncate">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 text-[12px]">{u.phone || '—'}</td>
                    <td className="px-4 py-2.5">
                      <Badge className="bg-slate-100 text-slate-700 border-0 capitalize text-[10.5px] font-semibold">{u.role}</Badge>
                    </td>
                    <td className="px-4 py-2.5">
                      {u.accountStatus === 'deleted' || u.is_deleted ? (
                        <Badge className="bg-red-100 text-red-700 border-0 text-[10.5px] font-semibold">Deleted</Badge>
                      ) : u.emailVerified === false ? (
                        <Badge className="bg-amber-100 text-amber-800 border-0 text-[10.5px] font-semibold">Pending</Badge>
                      ) : u.isDemo ? (
                        <Badge className="bg-amber-100 text-amber-800 border-0 text-[10.5px] font-semibold">Demo</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10.5px] font-semibold">
                          <span className="h-1 w-1 rounded-full bg-emerald-500 inline-block mr-1.5" />
                          Active
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 text-[11.5px]">{u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                    {(roleFilter === 'worker' || roleFilter === 'manager') && (
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        {roleFilter === 'worker' && workerTab === 'pending' ? (
                          <div className="inline-flex gap-1.5">
                            <Button
                              size="sm"
                              onClick={(e) => handleApprove(e, u)}
                              disabled={!!rowBusy[u.id]}
                              className="h-7 px-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px]"
                              data-testid={`worker-approve-btn-${u.id}`}
                            >
                              {rowBusy[u.id] === 'approve' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Approve'}
                            </Button>
                            <Button
                              size="sm"
                              onClick={(e) => handleReject(e, u)}
                              disabled={!!rowBusy[u.id]}
                              variant="outline"
                              className="h-7 px-2.5 rounded-full text-red-600 border-red-200 hover:bg-red-50 text-[11px]"
                              data-testid={`worker-reject-btn-${u.id}`}
                            >
                              {rowBusy[u.id] === 'reject' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Reject'}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(u); }}
                            className="h-7 px-2.5 rounded-full text-red-600 border-red-200 hover:bg-red-50 text-[11px]"
                            data-testid={`admin-delete-user-btn-${u.id}`}
                          >
                            Delete
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Worker / Add Manager modal */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="admin-add-user-modal">
          <DialogHeader>
            <DialogTitle>
              Add {roleFilter === 'manager' ? 'Manager' : 'Worker'}
            </DialogTitle>
            <DialogDescription>
              The account is created instantly and a welcome email is sent with a one-time link to set a password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] font-medium text-slate-500">Full name</Label>
              <Input
                value={addForm.fullName}
                onChange={(e) => setAddForm((f) => ({ ...f, fullName: e.target.value }))}
                className="mt-1"
                data-testid="add-user-fullname-input"
              />
            </div>
            <div>
              <Label className="text-[11px] font-medium text-slate-500">Email</Label>
              <Input
                type="email"
                value={addForm.email}
                onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                className="mt-1"
                data-testid="add-user-email-input"
              />
            </div>
            <div>
              <Label className="text-[11px] font-medium text-slate-500">Phone number</Label>
              <Input
                type="tel"
                value={addForm.phone}
                onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))}
                className="mt-1"
                data-testid="add-user-phone-input"
              />
            </div>
            <div>
              <Label className="text-[11px] font-medium text-slate-500">
                Branch {roleFilter === 'manager' ? '' : '(optional)'}
              </Label>
              <Input
                value={addForm.branchName}
                onChange={(e) => setAddForm((f) => ({ ...f, branchName: e.target.value }))}
                className="mt-1"
                placeholder={roleFilter === 'manager' ? 'e.g. High Street Post Office' : 'e.g. High Street Post Office'}
                data-testid="add-user-branch-input"
              />
            </div>
            {roleFilter === 'worker' && (
              <div>
                <Label className="text-[11px] font-medium text-slate-500">Role</Label>
                <Input value="Worker" disabled className="mt-1 bg-slate-50" />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="rounded-full" onClick={() => setAddModalOpen(false)} disabled={addBusy}>
              Cancel
            </Button>
            <Button
              onClick={submitAddUser}
              disabled={addBusy}
              className="rounded-full bg-[#0f1b3d] hover:bg-[#1e2e5e] text-white"
              data-testid="add-user-submit-btn"
            >
              {addBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
              Create account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete modal */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="admin-delete-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Delete this account?
            </DialogTitle>
            <DialogDescription>
              {deleteTarget && (
                <>This will deactivate <span className="font-semibold">{deleteTarget.fullName || deleteTarget.email}</span> and remove them from the active list.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <p className="text-[13px] text-slate-600 leading-relaxed">
            All associated data is retained silently on the backend. For any data queries direct the user to{' '}
            <a className="text-[#4338CA] underline" href="mailto:support@shiftcover365.co.uk">support@shiftcover365.co.uk</a>.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="rounded-full" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="rounded-full bg-red-600 hover:bg-red-700"
              onClick={submitDelete}
              disabled={deleteBusy}
              data-testid="admin-delete-confirm-btn"
            >
              {deleteBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              Yes, delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== SHIFTS =====================

function ShiftsPanel({ api, initialFilter, onOpenDrawer }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const validStatus = ['all', 'open', 'active', 'completed', 'cancelled'];
  const [statusFilter, setStatusFilter] = useState(validStatus.includes(initialFilter) ? initialFilter : 'all');
  // Special: 'till-alerts', 'paid', 'payment-failed' filtered client-side
  const [specialFilter, setSpecialFilter] = useState(
    ['till-alerts', 'paid', 'payment-failed'].includes(initialFilter) ? initialFilter : null
  );

  useEffect(() => {
    if (!initialFilter) return;
    if (validStatus.includes(initialFilter)) { setStatusFilter(initialFilter); setSpecialFilter(null); }
    else if (['till-alerts', 'paid', 'payment-failed'].includes(initialFilter)) { setSpecialFilter(initialFilter); setStatusFilter('all'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
      const r = await api.get(`/admin/shifts${params}`);
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load shifts'));
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const displayItems = useMemo(() => {
    let arr = items;
    if (specialFilter === 'till-alerts') arr = arr.filter((s) => s.needsReview);
    else if (specialFilter === 'paid') arr = arr.filter((s) => s.shiftPaymentStatus === 'paid');
    else if (specialFilter === 'payment-failed') arr = arr.filter((s) => s.shiftPaymentStatus === 'payment_failed');
    return arr;
  }, [items, specialFilter]);

  const specialLabels = { 'till-alerts': 'Till alerts only', paid: 'Paid only', 'payment-failed': 'Payment failed only' };

  return (
    <div className="space-y-4" data-testid="admin-shifts-panel">
      <div className="flex flex-wrap gap-2">
        {[
          ['all', `All (${counts.all ?? 0})`],
          ['open', `Open (${counts.open ?? 0})`],
          ['active', `Active (${counts.active ?? 0})`],
          ['completed', `Completed (${counts.completed ?? 0})`],
        ].map(([v, lbl]) => (
          <button
            key={v}
            onClick={() => { setStatusFilter(v); setSpecialFilter(null); }}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all
                        ${statusFilter === v && !specialFilter ? 'bg-[#0f1b3d] text-white border-[#0f1b3d]' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}
            data-testid={`shifts-filter-${v}`}
          >
            {lbl}
          </button>
        ))}
        {specialFilter && (
          <button
            onClick={() => setSpecialFilter(null)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-[#0f1b3d] text-white hover:bg-[#1e2e5e]"
            data-testid="admin-shifts-special-filter-clear"
          >
            {specialLabels[specialFilter]} <X className="h-3 w-3" />
          </button>
        )}
        <span className="ml-auto text-xs text-slate-500 self-center">Demo shifts in DB: {counts.demo ?? 0}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
      ) : displayItems.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white">
          <EmptyState
            icon={FileText}
            text={specialFilter ? `No shifts match "${specialLabels[specialFilter]}".` : (statusFilter !== 'all' ? `No ${statusFilter} shifts right now.` : 'No shifts found.')}
            action={(specialFilter || statusFilter !== 'all') && (
              <Button onClick={() => { setSpecialFilter(null); setStatusFilter('all'); }} variant="outline" size="sm" className="rounded-full h-8 text-[12px] border-slate-300">
                Clear filters
              </Button>
            )}
          />
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50/80 backdrop-blur text-left text-[10.5px] text-slate-500 uppercase tracking-[0.08em] sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">Branch</th>
                  <th className="px-4 py-2.5 font-semibold">Manager</th>
                  <th className="px-4 py-2.5 font-semibold">Date</th>
                  <th className="px-4 py-2.5 font-semibold">Pay</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold">Payment</th>
                </tr>
              </thead>
              <tbody>
                {displayItems.map((s) => (
                  <tr
                    key={s.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpenDrawer && onOpenDrawer('shift', s)}
                    onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && onOpenDrawer) { e.preventDefault(); onOpenDrawer('shift', s); } }}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer focus:outline-none focus:bg-slate-50 transition-colors"
                    data-testid={`admin-shift-row-${s.id}`}
                  >
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-slate-900">{s.branchName}</p>
                      {s.isDemo && <span className="text-[10px] text-amber-700 font-medium">Demo</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <Avatar name={s.managerName || s.managerEmail} size="xs" />
                        <div className="min-w-0">
                          <p className="text-slate-700 truncate text-[12.5px]">{s.managerName || '—'}</p>
                          <p className="text-[10.5px] text-slate-400 truncate max-w-[160px]">{s.managerEmail}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{s.date}</td>
                    <td className="px-4 py-2.5 text-slate-900 font-semibold tabular-nums">£{s.pay}</td>
                    <td className="px-4 py-2.5"><ShiftStatusPill status={s.status} /></td>
                    <td className="px-4 py-2.5"><PaymentStatusPill status={s.shiftPaymentStatus} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


/* ============== INCIDENTS PANEL ============== */

const SEV_OPTIONS = [
  { value: 'minor',    label: 'Minor',    tone: 'bg-amber-100 text-amber-800' },
  { value: 'major',    label: 'Major',    tone: 'bg-orange-100 text-orange-800' },
  { value: 'critical', label: 'Critical', tone: 'bg-red-100 text-red-800' },
];

const INCIDENT_STATUS_OPTIONS = [
  { value: 'investigating', label: 'Investigating', tone: 'bg-amber-100 text-amber-800' },
  { value: 'identified',    label: 'Identified',    tone: 'bg-indigo-100 text-indigo-800' },
  { value: 'monitoring',    label: 'Monitoring',    tone: 'bg-blue-100 text-blue-800' },
  { value: 'resolved',      label: 'Resolved',      tone: 'bg-emerald-100 text-emerald-700' },
];

const COMPONENT_OPTIONS = ['API', 'Database', 'Email', 'Payments'];

function IncidentsPanel({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editIncident, setEditIncident] = useState(null);
  const [updateIncident, setUpdateIncident] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/incidents');
      setItems(res.data.items || []);
    } catch (e) {
      setError(formatApiError(e, 'Could not load incidents'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  const handleResolve = async (incident) => {
    try {
      await api.post(`/admin/incidents/${incident.id}/updates`, {
        status: 'resolved',
        message: 'Issue resolved.',
      });
      toast.success('Incident marked as resolved');
      reload();
    } catch (e) {
      toast.error(formatApiError(e, 'Could not resolve incident'));
    }
  };

  const active = items.filter((i) => i.status !== 'resolved');
  const past = items.filter((i) => i.status === 'resolved');

  return (
    <div className="space-y-5" data-testid="admin-incidents-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">Incidents</h2>
          <p className="text-[13px] text-slate-500 mt-0.5">Publish status updates that appear publicly on /status.</p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="rounded-full bg-slate-900 hover:bg-slate-800 text-white h-10 px-4 text-[13px] font-semibold inline-flex items-center gap-2"
          data-testid="incident-create-btn"
        >
          <Plus className="h-4 w-4" /> New incident
        </Button>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="incident-stats">
        <Stat label="Active" value={active.length} tone="amber" />
        <Stat label="Resolved" value={past.length} tone="emerald" />
        <Stat label="Critical (90d)" value={countSev(past, 'critical', 90)} tone="red" />
        <Stat label="Total" value={items.length} tone="slate" />
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-800">{error}</div>
      )}

      {/* Active */}
      <SectionHeader title="Active" count={active.length} tone="amber" />
      <IncidentTable
        rows={active}
        loading={loading}
        emptyText="No active incidents — all clear."
        onEdit={setEditIncident}
        onUpdate={setUpdateIncident}
        onResolve={handleResolve}
      />

      {/* History */}
      <SectionHeader title="History" count={past.length} tone="slate" />
      <IncidentTable
        rows={past}
        loading={loading}
        emptyText="No past incidents recorded."
        onEdit={setEditIncident}
        onUpdate={null}
        onResolve={null}
      />

      {/* Create modal */}
      <IncidentCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        api={api}
        onCreated={() => { setCreateOpen(false); reload(); }}
      />

      {/* Edit metadata modal */}
      <IncidentEditDialog
        incident={editIncident}
        onClose={() => setEditIncident(null)}
        api={api}
        onSaved={() => { setEditIncident(null); reload(); }}
      />

      {/* Add update modal */}
      <IncidentUpdateDialog
        incident={updateIncident}
        onClose={() => setUpdateIncident(null)}
        api={api}
        onSaved={() => { setUpdateIncident(null); reload(); }}
      />
    </div>
  );
}

function countSev(rows, sev, days) {
  const since = Date.now() - days * 86400000;
  return rows.filter((r) => r.severity === sev && new Date(r.startedAt).getTime() >= since).length;
}

function SectionHeader({ title, count, tone }) {
  const cls = tone === 'amber' ? 'text-amber-700' : 'text-slate-500';
  return (
    <h3 className={`mt-4 text-[11px] font-semibold uppercase tracking-[0.14em] ${cls}`}>
      {title} <span className="text-slate-400">· {count}</span>
    </h3>
  );
}

function Stat({ label, value, tone }) {
  const accents = {
    amber:   'bg-amber-50 text-amber-700 border-amber-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    red:     'bg-red-50 text-red-700 border-red-100',
    slate:   'bg-slate-100 text-slate-700 border-slate-200',
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</p>
      <p className="mt-1.5 text-2xl font-bold tabular-nums text-slate-900">{value}</p>
      <span className={`mt-1.5 inline-flex items-center rounded-full border text-[10.5px] font-medium px-2 h-5 ${accents[tone] || accents.slate}`}>
        {tone === 'amber' ? 'Now' : '90 day view'}
      </span>
    </div>
  );
}

function IncidentTable({ rows, loading, emptyText, onEdit, onUpdate, onResolve }) {
  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center justify-center text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-[13.5px] text-slate-500">
        {emptyText}
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-100 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
            <tr>
              <th className="px-5 py-3">Title</th>
              <th className="px-5 py-3">Severity</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Components</th>
              <th className="px-5 py-3">Started</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((inc) => (
              <tr key={inc.id} className="text-[13.5px] hover:bg-slate-50/60" data-testid={`incident-row-${inc.id}`}>
                <td className="px-5 py-3 align-top">
                  <p className="font-semibold text-slate-900">{inc.title}</p>
                  <p className="text-[11.5px] text-slate-500 mt-0.5">{(inc.updates || []).length} update{(inc.updates || []).length === 1 ? '' : 's'}</p>
                </td>
                <td className="px-5 py-3 align-top"><SeverityPill severity={inc.severity} /></td>
                <td className="px-5 py-3 align-top"><IncidentStatusPill status={inc.status} /></td>
                <td className="px-5 py-3 align-top">
                  <div className="flex flex-wrap gap-1">
                    {(inc.affectedComponents || []).map((c) => (
                      <span key={c} className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 text-[10.5px] font-medium px-2 h-5">{c}</span>
                    ))}
                  </div>
                </td>
                <td className="px-5 py-3 align-top text-[12.5px] text-slate-500 whitespace-nowrap">
                  {inc.startedAt ? new Date(inc.startedAt).toLocaleString('en-GB') : '—'}
                </td>
                <td className="px-5 py-3 align-top text-right">
                  <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                    {onUpdate && (
                      <Button variant="ghost" size="sm" className="h-8 px-3 text-[12px]" onClick={() => onUpdate(inc)} data-testid={`incident-add-update-${inc.id}`}>
                        Add update
                      </Button>
                    )}
                    {onResolve && (
                      <Button size="sm" className="h-8 px-3 text-[12px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onResolve(inc)} data-testid={`incident-resolve-${inc.id}`}>
                        Resolve
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="h-8 px-3 text-[12px]" onClick={() => onEdit(inc)} data-testid={`incident-edit-${inc.id}`}>
                      Edit
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-slate-100">
        {rows.map((inc) => (
          <div key={inc.id} className="p-4">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <SeverityPill severity={inc.severity} />
              <IncidentStatusPill status={inc.status} />
            </div>
            <p className="font-semibold text-slate-900 text-[14px]">{inc.title}</p>
            <p className="text-[11.5px] text-slate-500 mt-0.5">
              Started {inc.startedAt ? new Date(inc.startedAt).toLocaleString('en-GB') : '—'} · {(inc.updates || []).length} update{(inc.updates || []).length === 1 ? '' : 's'}
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {onUpdate && <Button size="sm" variant="outline" className="h-8 text-[12px]" onClick={() => onUpdate(inc)}>Add update</Button>}
              {onResolve && <Button size="sm" className="h-8 text-[12px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onResolve(inc)}>Resolve</Button>}
              <Button size="sm" variant="ghost" className="h-8 text-[12px]" onClick={() => onEdit(inc)}>Edit</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SeverityPill({ severity }) {
  const o = SEV_OPTIONS.find((x) => x.value === (severity || 'minor').toLowerCase()) || SEV_OPTIONS[0];
  return <Badge className={`${o.tone} border-0 text-[11px] font-semibold uppercase tracking-[0.06em]`}>{o.label}</Badge>;
}

function IncidentStatusPill({ status }) {
  const o = INCIDENT_STATUS_OPTIONS.find((x) => x.value === status) || INCIDENT_STATUS_OPTIONS[0];
  return <Badge className={`${o.tone} border-0 text-[11px] font-semibold uppercase tracking-[0.06em]`}>{o.label}</Badge>;
}

function IncidentCreateDialog({ open, onClose, api, onCreated }) {
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState('minor');
  const [components, setComponents] = useState([]);
  const [initialMessage, setInitialMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(''); setSeverity('minor'); setComponents([]); setInitialMessage('');
    }
  }, [open]);

  const toggleComponent = (c) => {
    setComponents((cur) => cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]);
  };

  const submit = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      await api.post('/admin/incidents', {
        title: title.trim(),
        severity,
        affectedComponents: components,
        initialMessage: initialMessage.trim(),
      });
      toast.success('Incident created');
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e, 'Could not create incident'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="incident-create-dialog">
        <DialogHeader>
          <DialogTitle>New incident</DialogTitle>
          <DialogDescription>This will appear immediately on the public /status page.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[12.5px] font-semibold text-slate-700">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Email delivery delays" data-testid="incident-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12.5px] font-semibold text-slate-700">Severity</Label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger data-testid="incident-severity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SEV_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[12.5px] font-semibold text-slate-700">Components affected</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {COMPONENT_OPTIONS.map((c) => (
                  <button
                    key={c} type="button" onClick={() => toggleComponent(c)}
                    className={`inline-flex items-center rounded-full border px-2.5 h-7 text-[11.5px] font-semibold transition-colors ${
                      components.includes(c)
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`incident-comp-toggle-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <Label className="text-[12.5px] font-semibold text-slate-700">Initial update message</Label>
            <Textarea
              value={initialMessage} onChange={(e) => setInitialMessage(e.target.value)}
              rows={3} placeholder="Describe what users are seeing and what you're doing."
              data-testid="incident-initial-message"
            />
            <p className="mt-1.5 text-[11.5px] text-slate-500">Optional but recommended.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="incident-create-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Publish incident
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IncidentEditDialog({ incident, onClose, api, onSaved }) {
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState('minor');
  const [components, setComponents] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (incident) {
      setTitle(incident.title || '');
      setSeverity(incident.severity || 'minor');
      setComponents(incident.affectedComponents || []);
    }
  }, [incident]);

  const toggleComponent = (c) => {
    setComponents((cur) => cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/incidents/${incident.id}`, {
        title: title.trim(),
        severity,
        affectedComponents: components,
      });
      toast.success('Incident updated');
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e, 'Could not update incident'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete incident "${incident.title}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await api.delete(`/admin/incidents/${incident.id}`);
      toast.success('Incident deleted');
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e, 'Could not delete incident'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!incident} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="incident-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit incident</DialogTitle>
          <DialogDescription>Adjust metadata or delete this record.</DialogDescription>
        </DialogHeader>
        {incident && (
          <div className="space-y-4">
            <div>
              <Label className="text-[12.5px] font-semibold text-slate-700">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12.5px] font-semibold text-slate-700">Severity</Label>
                <Select value={severity} onValueChange={setSeverity}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SEV_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12.5px] font-semibold text-slate-700">Components</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {COMPONENT_OPTIONS.map((c) => (
                    <button
                      key={c} type="button" onClick={() => toggleComponent(c)}
                      className={`inline-flex items-center rounded-full border px-2.5 h-7 text-[11.5px] font-semibold transition-colors ${
                        components.includes(c)
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Updates timeline */}
            <div>
              <Label className="text-[12.5px] font-semibold text-slate-700">Updates timeline ({(incident.updates || []).length})</Label>
              <ol className="mt-2 max-h-48 overflow-y-auto pr-1 space-y-2.5">
                {(incident.updates || []).slice().reverse().map((u) => (
                  <li key={u.id} className="flex gap-3">
                    <span className="mt-1.5 h-2 w-2 rounded-full bg-slate-400 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                        {u.status} · {u.createdAt ? new Date(u.createdAt).toLocaleString('en-GB') : ''}
                      </div>
                      <p className="mt-0.5 text-[12.5px] text-slate-800 leading-relaxed">{u.message}</p>
                    </div>
                  </li>
                ))}
                {(incident.updates || []).length === 0 && (
                  <li className="text-[12.5px] text-slate-500">No updates posted yet.</li>
                )}
              </ol>
            </div>
          </div>
        )}
        <DialogFooter className="flex sm:justify-between">
          <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={remove} disabled={saving} data-testid="incident-delete-btn">
            Delete
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="incident-edit-submit">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IncidentUpdateDialog({ incident, onClose, api, onSaved }) {
  const [status, setStatus] = useState('investigating');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (incident) {
      setStatus(incident.status === 'resolved' ? 'monitoring' : incident.status || 'investigating');
      setMessage('');
    }
  }, [incident]);

  const submit = async () => {
    if (!message.trim()) { toast.error('Update message is required'); return; }
    setSaving(true);
    try {
      await api.post(`/admin/incidents/${incident.id}/updates`, {
        status,
        message: message.trim(),
      });
      toast.success('Update posted');
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e, 'Could not post update'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!incident} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="incident-update-dialog">
        <DialogHeader>
          <DialogTitle>Post an update</DialogTitle>
          <DialogDescription>
            {incident?.title ? <>For <span className="font-semibold">{incident.title}</span></> : null}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[12.5px] font-semibold text-slate-700">New status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="incident-update-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INCIDENT_STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[12.5px] font-semibold text-slate-700">Message</Label>
            <Textarea
              value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
              placeholder="What's new? Keep it short and factual." data-testid="incident-update-message"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="incident-update-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Post update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============== END INCIDENTS PANEL ============== */


function ShiftStatusPill({ status }) {
  const map = {
    open: { label: 'Open', tone: 'bg-blue-100 text-blue-700' },
    active: { label: 'Active', tone: 'bg-emerald-100 text-emerald-700' },
    in_progress: { label: 'In progress', tone: 'bg-emerald-100 text-emerald-700' },
    completed: { label: 'Completed', tone: 'bg-slate-100 text-slate-700' },
    cancelled: { label: 'Cancelled', tone: 'bg-red-100 text-red-700' },
  };
  const m = map[status] || { label: status || '—', tone: 'bg-slate-100 text-slate-700' };
  return <Badge className={`${m.tone} border-0 text-[10.5px] font-semibold capitalize`}>{m.label}</Badge>;
}

function PaymentStatusPill({ status }) {
  if (!status) return <span className="text-[11px] text-slate-400">—</span>;
  const map = {
    paid: { label: 'Paid', tone: 'bg-emerald-100 text-emerald-700' },
    pending: { label: 'Pending', tone: 'bg-amber-100 text-amber-700' },
    payment_failed: { label: 'Failed', tone: 'bg-red-100 text-red-700' },
  };
  const m = map[status] || { label: status, tone: 'bg-slate-100 text-slate-700' };
  return <Badge className={`${m.tone} border-0 text-[10.5px] font-semibold`}>{m.label}</Badge>;
}

// ===================== DETAIL DRAWER =====================

// ===================== BRANCHES =====================
function BranchesPanel({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get('/admin/branches');
        if (!cancel) setItems(r.data.items || []);
      } catch (e) {
        toast.error(formatApiError(e, 'Failed to load branches'));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [api]);

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  }
  return (
    <div className="space-y-4" data-testid="admin-branches-panel">
      <p className="text-[12.5px] text-slate-500">
        Real Post Office branches with at least one enrolled manager. Demo accounts excluded.
      </p>
      {items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white">
          <EmptyState
            icon={Building}
            text="No branches enrolled yet. A branch appears here when a manager completes signup with their business name."
          />
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Branch</th>
                <th className="text-left px-4 py-3">Manager</th>
                <th className="text-left px-4 py-3">Postcode</th>
                <th className="text-left px-4 py-3">Enrolled</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((b, idx) => (
                <tr key={b.name} className="hover:bg-slate-50" data-testid={`branch-row-${idx}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{b.name}</td>
                  <td className="px-4 py-3 text-slate-700">
                    {b.managerName || b.managerEmail || '—'}
                    {b.managerEmail && b.managerName && <div className="text-[11px] text-slate-400">{b.managerEmail}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{b.postcode || '—'}</td>
                  <td className="px-4 py-3 text-slate-500 text-[12px]">{relativeDate(b.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ===================== PAYMENTS =====================
function PaymentsPanel({ api, initialFilter }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialFilter || 'all'); // all | failed | penalties

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get('/admin/payments?limit=500');
        if (!cancel) setItems(r.data.items || []);
      } catch (e) {
        toast.error(formatApiError(e, 'Failed to load payments'));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [api]);

  const filtered = useMemo(() => {
    if (filter === 'failed') return items.filter((t) => ['failed', 'past_due'].includes(t.paymentStatus));
    if (filter === 'penalties') return items.filter((t) => (t.type || '').toLowerCase().includes('penalty'));
    return items;
  }, [items, filter]);

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  }

  const tabs = [
    { key: 'all', label: `All (${items.length})` },
    { key: 'failed', label: `Failed (${items.filter((t) => ['failed', 'past_due'].includes(t.paymentStatus)).length})` },
    { key: 'penalties', label: `Penalties (${items.filter((t) => (t.type || '').toLowerCase().includes('penalty')).length})` },
  ];

  return (
    <div className="space-y-4" data-testid="admin-payments-panel">
      <div className="flex gap-2 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${filter === t.key ? 'bg-[#0f1b3d] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:text-slate-900'}`}
            data-testid={`payments-filter-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white">
          <EmptyState icon={CreditCard} text="No payments yet. Transactions will appear here as real managers and workers are charged." />
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((t, idx) => (
                <tr key={t.id || idx} className="hover:bg-slate-50" data-testid={`payment-row-${idx}`}>
                  <td className="px-4 py-3 text-slate-500 text-[12px]">{relativeDate(t.createdAt)}</td>
                  <td className="px-4 py-3 text-slate-700">
                    {t.userName || t.userEmail || '—'}
                    {t.userRole && <div className="text-[11px] text-slate-400">{t.userRole}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{t.type || '—'}</td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums">£{Number(t.amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <PaymentStatusBadge status={t.paymentStatus} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PaymentStatusBadge({ status }) {
  const map = {
    paid: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-emerald-100 text-emerald-700',
    pending: 'bg-amber-100 text-amber-700',
    failed: 'bg-red-100 text-red-700',
    past_due: 'bg-red-100 text-red-700',
    refunded: 'bg-slate-100 text-slate-700',
  };
  const cls = map[status] || 'bg-slate-100 text-slate-700';
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>{status || 'unknown'}</span>;
}

// ===================== DATA & COMPLIANCE =====================
function DataCompliancePanel({ api }) {
  const [tab, setTab] = useState('deletion-requests');
  const tabs = [
    { key: 'deletion-requests', label: 'Deletion Requests' },
    { key: 'sars', label: 'Subject Access Requests' },
    { key: 'breaches', label: 'Data Breach Log' },
    { key: 'verification', label: 'Verification Audit Trail' },
    { key: 'consent', label: 'Consent Audit' },
    { key: 'retention', label: 'Data Retention Policy' },
  ];
  return (
    <div className="space-y-4" data-testid="admin-data-compliance-panel">
      <div className="flex gap-2 overflow-x-auto -mx-1 px-1">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${tab === t.key ? 'bg-[#0f1b3d] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:text-slate-900'}`}
            data-testid={`compliance-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'deletion-requests' && <DeletionRequestsTab api={api} />}
      {tab === 'sars' && <SARsTab api={api} />}
      {tab === 'breaches' && <BreachesTab api={api} />}
      {tab === 'verification' && <VerificationAuditTab api={api} />}
      {tab === 'consent' && <ConsentAuditTab api={api} />}
      {tab === 'retention' && <RetentionPolicyTab api={api} />}
    </div>
  );
}

function DeletionRequestsTab({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [detail, setDetail] = useState(null); // {request, sarHistory}
  const [decisionModal, setDecisionModal] = useState(null); // {kind:'approve'|'reject', request}
  const [decisionReason, setDecisionReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const q = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
      const r = await api.get(`/admin/deletion-requests${q}`);
      setItems(r.data.requests || []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load deletion requests'));
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (req) => {
    try {
      const r = await api.get(`/admin/deletion-requests/${req.id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load request detail'));
    }
  };

  const submitDecision = async () => {
    if (!decisionModal) return;
    if (decisionModal.kind === 'reject' && !decisionReason.trim()) {
      toast.error('A reason is required to reject a deletion request.');
      return;
    }
    setBusy(true);
    try {
      const path = decisionModal.kind === 'approve' ? 'approve' : 'reject';
      const res = await api.post(
        `/admin/deletion-requests/${decisionModal.request.id}/${path}`,
        { reason: decisionReason.trim() || undefined }
      );
      if (decisionModal.kind === 'approve') {
        toast.success(
          res.data?.erasureExecuted
            ? `Erasure complete. ${res.data?.paymentsPreserved || 0} payment record(s) preserved (HMRC).`
            : 'Request approved. No matching user row to erase.'
        );
      } else {
        toast.success('Request rejected. Audit row written.');
      }
      setDecisionModal(null);
      setDecisionReason('');
      load();
    } catch (e) {
      toast.error(formatApiError(e, 'Action failed'));
    } finally {
      setBusy(false);
    }
  };

  const statusBadge = (s) => {
    const map = {
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      approved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      rejected: 'bg-slate-100 text-slate-700 border-slate-200',
    };
    return map[s] || map.pending;
  };

  return (
    <div className="space-y-4" data-testid="admin-deletion-requests-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900">GDPR Deletion Requests</h2>
          <p className="text-[12px] text-slate-500">
            UK GDPR Article 17 requires response within one month. Days remaining is highlighted in red when overdue.
          </p>
        </div>
        <div className="flex gap-2">
          {['pending', 'approved', 'rejected', 'all'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 h-9 rounded-full text-[12px] font-semibold capitalize ${
                statusFilter === s
                  ? 'bg-[#0f1b3d] text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:text-slate-900'
              }`}
              data-testid={`deletion-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-slate-500 text-sm">Loading...</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm" data-testid="deletion-requests-empty">
              No {statusFilter === 'all' ? '' : statusFilter} deletion requests.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">Requested</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Days Left</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`deletion-row-${r.id}`}>
                      <td className="px-4 py-3 text-[12.5px] text-slate-700">
                        {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-[12.5px] text-slate-900 font-medium">
                        {r.userFullName || r.fullName || '—'}
                      </td>
                      <td className="px-4 py-3 text-[12.5px] text-slate-700">{r.email || '—'}</td>
                      <td className="px-4 py-3 text-[12.5px] capitalize text-slate-700">
                        {r.userRole || r.role || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold border capitalize ${statusBadge(r.status)}`}>
                          {r.status || 'pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[12.5px] tabular-nums">
                        {r.status === 'pending' ? (
                          <span className={r.isOverdue ? 'text-red-600 font-bold' : 'text-slate-700'}>
                            {r.daysRemaining != null ? `${r.daysRemaining}d` : '—'}
                            {r.isOverdue && ' overdue'}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Button
                          variant="ghost" size="sm"
                          className="h-8 px-2 text-[11.5px]"
                          onClick={() => openDetail(r)}
                          data-testid={`view-deletion-${r.id}`}
                        >View</Button>
                        {r.status === 'pending' && (
                          <>
                            <Button
                              variant="ghost" size="sm"
                              className="h-8 px-2 text-[11.5px] text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                              onClick={() => { setDecisionModal({ kind: 'approve', request: r }); setDecisionReason(''); }}
                              data-testid={`approve-deletion-${r.id}`}
                            >Approve</Button>
                            <Button
                              variant="ghost" size="sm"
                              className="h-8 px-2 text-[11.5px] text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => { setDecisionModal({ kind: 'reject', request: r }); setDecisionReason(''); }}
                              data-testid={`reject-deletion-${r.id}`}
                            >Reject</Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail / Audit-history dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="deletion-detail-modal">
          <DialogHeader>
            <DialogTitle>Deletion request detail</DialogTitle>
          </DialogHeader>
          {detail?.request && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name" value={detail.request.fullName || detail.request.userFullName || '—'} />
                <Field label="Email" value={detail.request.email || '—'} />
                <Field label="Role" value={detail.request.userRole || detail.request.role || '—'} />
                <Field label="Status" value={detail.request.status || 'pending'} />
                <Field label="Requested" value={detail.request.createdAt || '—'} />
                <Field label="Source" value={detail.request.source || 'public_form'} />
                {detail.request.approvedAt && <Field label="Approved" value={detail.request.approvedAt} />}
                {detail.request.approvedBy && <Field label="Approved by" value={detail.request.approvedBy} />}
                {detail.request.rejectedAt && <Field label="Rejected" value={detail.request.rejectedAt} />}
                {detail.request.rejectedBy && <Field label="Rejected by" value={detail.request.rejectedBy} />}
              </div>
              {detail.request.reason && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">User reason</p>
                  <p className="text-[13px] text-slate-700 leading-relaxed">{detail.request.reason}</p>
                </div>
              )}
              {detail.request.rejectionReason && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Admin rejection reason</p>
                  <p className="text-[13px] text-slate-700 leading-relaxed">{detail.request.rejectionReason}</p>
                </div>
              )}
              {detail.request.erasureSummary && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Erasure cascade summary</p>
                  <pre className="text-[11.5px] bg-slate-50 p-3 rounded-lg overflow-x-auto">{JSON.stringify(detail.request.erasureSummary, null, 2)}</pre>
                  <p className="text-[11.5px] text-slate-500 mt-1">
                    Payments preserved (HMRC 7-year retention): {detail.request.paymentsPreserved ?? 0}
                  </p>
                </div>
              )}
              {detail.sarHistory && detail.sarHistory.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Audit history (SAR rows)</p>
                  <div className="space-y-2">
                    {detail.sarHistory.map((s) => (
                      <div key={s.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[12px]">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold capitalize">{s.requestType} — {s.status}</span>
                          <span className="text-slate-500">{new Date(s.createdAt).toLocaleString()}</span>
                        </div>
                        {s.fulfilledBy && <p className="text-slate-600 mt-1">Fulfilled by {s.fulfilledBy}</p>}
                        {s.rejectionReason && <p className="text-slate-600 mt-1">Reason: {s.rejectionReason}</p>}
                        {s.notes && <p className="text-slate-500 mt-1 italic">{s.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve / Reject dialog */}
      <Dialog open={!!decisionModal} onOpenChange={(o) => !o && setDecisionModal(null)}>
        <DialogContent className="sm:max-w-md" data-testid="deletion-decision-modal">
          <DialogHeader>
            <DialogTitle>
              {decisionModal?.kind === 'approve' ? 'Approve erasure?' : 'Reject deletion request?'}
            </DialogTitle>
            <DialogDescription>
              {decisionModal?.kind === 'approve'
                ? 'This runs the Right-to-Erasure cascade immediately. Payment transactions are preserved (HMRC 7-year retention).'
                : 'Rejection is recorded in the audit trail. Provide a clear reason (e.g., legal hold, active fraud investigation).'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="decision-reason" className="text-[12.5px] font-medium">
              {decisionModal?.kind === 'approve' ? 'Note (optional)' : 'Reason (required)'}
            </Label>
            <Textarea
              id="decision-reason"
              rows={3}
              value={decisionReason}
              onChange={(e) => setDecisionReason(e.target.value.slice(0, 500))}
              placeholder={decisionModal?.kind === 'approve' ? 'Optional admin note' : 'e.g. Open dispute on shift #1234 — defer 30 days'}
              data-testid="decision-reason-input"
            />
            <p className="text-[11px] text-slate-400 text-right">{decisionReason.length}/500</p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="rounded-full" onClick={() => setDecisionModal(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              className={`rounded-full ${decisionModal?.kind === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}
              onClick={submitDecision}
              disabled={busy || (decisionModal?.kind === 'reject' && !decisionReason.trim())}
              data-testid="confirm-decision-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {decisionModal?.kind === 'approve' ? 'Approve & erase' : 'Reject request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">{label}</p>
      <p className="text-[12.5px] text-slate-900 break-all">{value}</p>
    </div>
  );
}

function SARsTab({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ requesterName: '', requesterEmail: '', requestType: 'access', notes: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get('/admin/sars');
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load SARs'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.requesterName.trim() || !form.requesterEmail.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setBusy(true);
    try {
      await api.post('/admin/sars', form);
      toast.success('SAR logged');
      setModal(false);
      setForm({ requesterName: '', requesterEmail: '', requestType: 'access', notes: '' });
      load();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to log SAR'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[12.5px] text-slate-500">Track subject access, erasure, and portability requests under UK GDPR.</p>
        <Button size="sm" className="rounded-full h-9 bg-[#0f1b3d] text-white" onClick={() => setModal(true)} data-testid="sar-log-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Log SAR
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white">
          <EmptyState icon={ShieldAlert} text="No subject access requests logged yet." />
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
          {items.map((s) => (
            <div key={s.id} className="px-4 py-3 flex items-center justify-between" data-testid={`sar-row-${s.id}`}>
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-slate-900 truncate">{s.requesterName} · {s.requesterEmail}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{s.requestType} · {relativeDate(s.createdAt)}</p>
              </div>
              <Badge className={`${s.status === 'closed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'} border-0`}>{s.status || 'open'}</Badge>
            </div>
          ))}
        </div>
      )}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="sm:max-w-md" data-testid="sar-modal">
          <DialogHeader>
            <DialogTitle>Log subject access request</DialogTitle>
            <DialogDescription>Record a request to access, port, or erase personal data.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] font-medium text-slate-500">Requester name</Label>
              <Input value={form.requesterName} onChange={(e) => setForm((f) => ({ ...f, requesterName: e.target.value }))} className="mt-1" data-testid="sar-name-input" />
            </div>
            <div>
              <Label className="text-[11px] font-medium text-slate-500">Requester email</Label>
              <Input type="email" value={form.requesterEmail} onChange={(e) => setForm((f) => ({ ...f, requesterEmail: e.target.value }))} className="mt-1" data-testid="sar-email-input" />
            </div>
            <div>
              <Label className="text-[11px] font-medium text-slate-500">Request type</Label>
              <Select value={form.requestType} onValueChange={(v) => setForm((f) => ({ ...f, requestType: v }))}>
                <SelectTrigger className="mt-1" data-testid="sar-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="access">Access</SelectItem>
                  <SelectItem value="erasure">Erasure (right to be forgotten)</SelectItem>
                  <SelectItem value="portability">Portability</SelectItem>
                  <SelectItem value="rectification">Rectification</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] font-medium text-slate-500">Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1" data-testid="sar-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(false)} className="rounded-full h-10">Cancel</Button>
            <Button onClick={submit} disabled={busy} className="rounded-full h-10 bg-[#0f1b3d] text-white" data-testid="sar-submit-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Log request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BreachesTab({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ title: '', severity: 'low', description: '', reportedToICO: false, affectedUsers: 0 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get('/admin/breaches');
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load breach log'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setBusy(true);
    try {
      await api.post('/admin/breaches', form);
      toast.success('Breach logged');
      setModal(false);
      setForm({ title: '', severity: 'low', description: '', reportedToICO: false, affectedUsers: 0 });
      load();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to log breach'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[12.5px] text-slate-500">Maintain a register of security incidents per UK GDPR Article 33.</p>
        <Button size="sm" className="rounded-full h-9 bg-[#DA291C] text-white" onClick={() => setModal(true)} data-testid="breach-log-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Log incident
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white">
          <EmptyState icon={ShieldCheck} text="No breaches recorded. Maintaining this empty log is the goal." />
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
          {items.map((b) => (
            <div key={b.id} className="px-4 py-3" data-testid={`breach-row-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-slate-900">{b.title}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {relativeDate(b.createdAt)} · {b.affectedUsers || 0} users affected · ICO reported: {b.reportedToICO ? 'Yes' : 'No'}
                  </p>
                </div>
                <Badge className={`border-0 ${b.severity === 'critical' || b.severity === 'high' ? 'bg-red-100 text-red-700' : b.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}`}>
                  {b.severity}
                </Badge>
              </div>
              {b.description && <p className="text-[12px] text-slate-600 mt-2">{b.description}</p>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="sm:max-w-md" data-testid="breach-modal">
          <DialogHeader>
            <DialogTitle>Log data breach / security incident</DialogTitle>
            <DialogDescription>Record per UK GDPR Article 33. Notify ICO within 72 hours where required.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] font-medium text-slate-500">Title</Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="mt-1" data-testid="breach-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] font-medium text-slate-500">Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm((f) => ({ ...f, severity: v }))}>
                  <SelectTrigger className="mt-1" data-testid="breach-severity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] font-medium text-slate-500">Users affected</Label>
                <Input type="number" min="0" value={form.affectedUsers} onChange={(e) => setForm((f) => ({ ...f, affectedUsers: Number(e.target.value) }))} className="mt-1" data-testid="breach-affected-input" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] font-medium text-slate-500">Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="mt-1" data-testid="breach-desc-input" />
            </div>
            <label className="flex items-center gap-2 text-[12.5px] text-slate-700">
              <input type="checkbox" checked={form.reportedToICO} onChange={(e) => setForm((f) => ({ ...f, reportedToICO: e.target.checked }))} data-testid="breach-ico-reported" />
              Reported to ICO
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(false)} className="rounded-full h-10">Cancel</Button>
            <Button onClick={submit} disabled={busy} className="rounded-full h-10 bg-[#DA291C] text-white" data-testid="breach-submit-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Log incident'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VerificationAuditTab({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get('/admin/verification-audit');
        if (!cancel) setItems(r.data.items || []);
      } catch (e) {
        toast.error(formatApiError(e, 'Failed to load verification audit'));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [api]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white">
        <EmptyState icon={UserCheck} text="No real workers verified yet. Verification events will appear as workers complete Smart ID checks." />
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead className="bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
          <tr>
            <th className="text-left px-4 py-3">Worker</th>
            <th className="text-left px-4 py-3">Registered</th>
            <th className="text-left px-4 py-3">Verified</th>
            <th className="text-left px-4 py-3">Verified at</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((u, idx) => (
            <tr key={u.userId} className="hover:bg-slate-50" data-testid={`verification-row-${idx}`}>
              <td className="px-4 py-3">
                <div className="font-medium text-slate-900">{u.fullName || u.email}</div>
                <div className="text-[11px] text-slate-400">{u.email}</div>
              </td>
              <td className="px-4 py-3 text-slate-500 text-[12px]">{relativeDate(u.registeredAt)}</td>
              <td className="px-4 py-3">
                {u.verified
                  ? <Badge className="bg-emerald-100 text-emerald-700 border-0">Verified</Badge>
                  : <Badge className="bg-amber-100 text-amber-700 border-0">Pending</Badge>}
              </td>
              <td className="px-4 py-3 text-slate-500 text-[12px]">{u.verifiedAt ? relativeDate(u.verifiedAt) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RetentionPolicyTab({ api }) {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get('/admin/retention-policy');
        if (!cancel) setPolicies(r.data.policies || []);
      } catch (e) {
        toast.error(formatApiError(e, 'Failed to load retention policy'));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [api]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-3">
      <p className="text-[12.5px] text-slate-500">How long each data category is kept and the legal basis.</p>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Data category</th>
              <th className="text-left px-4 py-3">Retention</th>
              <th className="text-left px-4 py-3">Legal basis</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {policies.map((p, idx) => (
              <tr key={idx} data-testid={`retention-row-${idx}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{p.category}</td>
                <td className="px-4 py-3 text-slate-700">{p.retention}</td>
                <td className="px-4 py-3 text-slate-500">{p.basis}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===================== SETTINGS =====================
function DataExportCard({ api }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const handleExport = async () => {
    const target = email.trim();
    if (!target) {
      toast.error('Enter an email to export');
      return;
    }
    setBusy(true);
    try {
      const res = await api.get(`/admin/sar-export?email=${encodeURIComponent(target)}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url;
      a.download = `sar-${target.replace('@', '-at-')}-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded — a SAR audit row was logged.');
    } catch (e) {
      let detail = 'Export failed';
      try {
        if (e.response?.data && e.response.data instanceof Blob) {
          const text = await e.response.data.text();
          try { detail = JSON.parse(text).detail || detail; } catch { detail = text || detail; }
        } else {
          detail = formatApiError(e, detail);
        }
      } catch { /* fall through with default */ }
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border border-slate-200" data-testid="data-export-card">
      <CardContent className="p-5 space-y-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Subject Access Request export</h3>
          <p className="text-[12px] text-slate-500 mt-0.5">
            One-click bundle of every record we hold for an email address (profile, shifts, ratings, chats, payments, etc.) as a ZIP of CSVs. A SAR audit row is logged automatically.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-[11px] font-medium text-slate-500">Subject email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="someone@example.com"
              className="mt-1"
              data-testid="data-export-email-input"
              onKeyDown={(e) => { if (e.key === 'Enter' && !busy) handleExport(); }}
            />
          </div>
          <Button
            onClick={handleExport}
            disabled={busy || !email.trim()}
            className="rounded-full h-10 bg-[#0f1b3d] text-white"
            data-testid="data-export-download-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1.5" />Export</>}
          </Button>
        </div>
        <p className="text-[11px] text-slate-400">
          Use to fulfil UK GDPR Subject Access Requests. Downloaded archive is generated on-the-fly and not stored.
        </p>
      </CardContent>
    </Card>
  );
}

function RightToErasureCard({ api }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const handleErase = async () => {
    const target = email.trim();
    if (!target) {
      toast.error('Enter the subject email');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/admin/right-to-erasure', { email: target, confirm: true });
      setLastResult(r.data);
      const total = Object.values(r.data.summary || {}).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0);
      toast.success(`Erasure complete — ${total} record(s) deleted. SAR row logged.`);
      setEmail('');
      setConfirm(false);
    } catch (e) {
      toast.error(formatApiError(e, 'Erasure failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border border-red-200" data-testid="right-to-erasure-card">
      <CardContent className="p-5 space-y-3">
        <div>
          <h3 className="text-sm font-bold text-red-700 flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4" /> Right to Erasure
          </h3>
          <p className="text-[12px] text-slate-500 mt-0.5">
            Permanently delete a user's profile and cascade to shifts, chats, and ratings. Payment transactions are preserved for the 7-year HMRC retention window. An erasure-type SAR row is auto-logged.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-[11px] font-medium text-slate-500">Subject email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="someone@example.com"
              className="mt-1"
              data-testid="erasure-email-input"
              onKeyDown={(e) => { if (e.key === 'Enter' && confirm && !busy) handleErase(); }}
            />
          </div>
          <Button
            onClick={() => (confirm ? handleErase() : setConfirm(true))}
            disabled={busy || !email.trim()}
            className="rounded-full h-10 bg-red-600 hover:bg-red-700 text-white"
            data-testid="erasure-erase-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : confirm ? 'Confirm erase' : 'Erase'}
          </Button>
        </div>
        {confirm && !busy && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-[12px] text-red-800" data-testid="erasure-confirm-warning">
            <p className="font-semibold mb-1">Are you sure?</p>
            <p>This is irreversible. Click "Confirm erase" to proceed or change the email to cancel.</p>
            <button
              type="button"
              onClick={() => setConfirm(false)}
              className="mt-1.5 underline text-[11.5px]"
              data-testid="erasure-cancel-btn"
            >
              Cancel
            </button>
          </div>
        )}
        {lastResult && (
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-[11.5px] text-slate-700" data-testid="erasure-last-result">
            <p className="font-semibold text-slate-900 mb-1">Last erasure summary</p>
            <ul className="space-y-0.5">
              {Object.entries(lastResult.summary || {}).map(([k, v]) => (
                <li key={k} className="flex justify-between">
                  <span>{k.replace(/_/g, ' ')}</span>
                  <span className="font-mono text-slate-900">{v}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[10.5px] text-slate-400">
              {lastResult.paymentsPreserved ?? 0} payment transaction(s) preserved for 7-year HMRC retention.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SettingsPanel({ api }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ico, setIco] = useState('');
  const [pilotEnd, setPilotEnd] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get('/admin/settings');
      setSettings(r.data);
      setIco(r.data.icoRegistrationNumber || '');
      setPilotEnd(r.data.pilotEndDate || '');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load settings'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setSaving(true);
    try {
      await api.patch('/admin/settings', payload);
      toast.success('Saved');
      load();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-4 max-w-3xl" data-testid="admin-settings-panel">
      <Card className="border border-slate-200">
        <CardContent className="p-5 space-y-4">
          <div>
            <h3 className="text-sm font-bold text-slate-900">ICO Registration</h3>
            <p className="text-[12px] text-slate-500 mt-0.5">Your Information Commissioner&apos;s Office registration number. Displayed on the Trust Center.</p>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-[11px] font-medium text-slate-500">Registration number</Label>
              <Input
                value={ico}
                onChange={(e) => setIco(e.target.value)}
                placeholder="ZA000000"
                className="mt-1"
                data-testid="settings-ico-input"
              />
            </div>
            <Button
              onClick={() => save({ icoRegistrationNumber: ico })}
              disabled={saving || ico === (settings.icoRegistrationNumber || '')}
              className="rounded-full h-10 bg-[#0f1b3d] text-white"
              data-testid="settings-ico-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-slate-200">
        <CardContent className="p-5 space-y-4">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Pilot end date</h3>
            <p className="text-[12px] text-slate-500 mt-0.5">Drives the T−14d and T+7d reminder emails to pilot branches.</p>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-[11px] font-medium text-slate-500">Date (YYYY-MM-DD)</Label>
              <Input
                type="date"
                value={pilotEnd}
                onChange={(e) => setPilotEnd(e.target.value)}
                className="mt-1"
                data-testid="settings-pilot-end-input"
              />
            </div>
            <Button
              onClick={() => save({ pilotEndDate: pilotEnd })}
              disabled={saving || pilotEnd === (settings.pilotEndDate || '')}
              className="rounded-full h-10 bg-[#0f1b3d] text-white"
              data-testid="settings-pilot-end-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-slate-200">
        <CardContent className="p-5 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900">Stripe configuration</h3>
            <Badge className={`${settings.stripe?.configured ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'} border-0`}>
              {settings.stripe?.configured ? 'Connected' : 'Not configured'}
            </Badge>
          </div>
          <p className="text-[12px] text-slate-500">Read-only. Update Stripe keys directly via backend environment variables.</p>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12.5px] mt-2">
            <dt className="text-slate-500">Mode</dt>
            <dd className="text-slate-900 font-medium">{settings.stripe?.mode || '—'}</dd>
            <dt className="text-slate-500">Publishable key</dt>
            <dd className="text-slate-900 font-mono text-[11.5px]">{settings.stripe?.publishableKeyMasked || '—'}</dd>
            <dt className="text-slate-500">Last updated</dt>
            <dd className="text-slate-900 font-medium">{settings.stripe?.lastUpdated ? relativeDate(settings.stripe.lastUpdated) : '—'}</dd>
          </dl>
        </CardContent>
      </Card>

      <Card className="border border-slate-200">
        <CardContent className="p-5">
          <h3 className="text-sm font-bold text-slate-900 mb-3">Email templates</h3>
          <p className="text-[12px] text-slate-500 mb-3">Sent via Resend from <span className="font-medium">{settings.support?.supportEmail}</span>. Edit by request — contact the dev team.</p>
          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
            {(settings.emailTemplates || []).map((t) => (
              <div key={t.key} className="px-4 py-2.5 flex items-center justify-between" data-testid={`email-template-${t.key}`}>
                <div>
                  <p className="text-[13px] font-medium text-slate-900">{t.name}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{t.subject}</p>
                </div>
                <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">Read-only</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <DataExportCard api={api} />
      <RightToErasureCard api={api} />
    </div>
  );
}

function DetailDrawer({ api, state, onClose }) {
  const { open, type, item } = state;
  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 overflow-y-auto" data-testid="admin-detail-drawer">
        {type === 'demo-request' && item && <DemoRequestDetail item={item} />}
        {type === 'user' && item && <UserDetail api={api} item={item} />}
        {type === 'shift' && item && <ShiftDetail item={item} />}
      </SheetContent>
    </Sheet>
  );
}

function DrawerSection({ title, children }) {
  return (
    <div className="px-5 py-4 border-b border-slate-100 last:border-0">
      <h4 className="text-[10.5px] font-semibold text-slate-500 uppercase tracking-[0.12em] mb-2.5">{title}</h4>
      {children}
    </div>
  );
}

function FieldRow({ label, value, mono = false }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 py-1">
      <span className="text-[12px] text-slate-500">{label}</span>
      <span className={`text-[12.5px] text-slate-900 ${mono ? 'font-mono tabular-nums' : ''} break-words`}>{value || <span className="text-slate-400">—</span>}</span>
    </div>
  );
}

function DemoRequestDetail({ item }) {
  return (
    <>
      <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
        <SheetTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <Avatar name={item.name} />
          {item.name}
        </SheetTitle>
        <SheetDescription className="text-[12px] text-slate-500">Demo request submitted {relativeDate(item.createdAt)}</SheetDescription>
      </SheetHeader>
      <DrawerSection title="Contact">
        <FieldRow label="Email" value={<a href={`mailto:${item.email}`} className="text-[#DA291C] hover:underline">{item.email}</a>} />
        <FieldRow label="Phone" value={item.phone ? <a href={`tel:${item.phone}`} className="text-[#DA291C] hover:underline">{item.phone}</a> : null} />
        <FieldRow label="Business" value={item.business} />
      </DrawerSection>
      <DrawerSection title="Status">
        <div className="flex items-center gap-2"><StatusBadge value={item.status} /></div>
      </DrawerSection>
      {item.message && (
        <DrawerSection title="Message">
          <p className="text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed bg-slate-50 rounded-lg p-3">{item.message}</p>
        </DrawerSection>
      )}
      <DrawerSection title="Metadata">
        <FieldRow label="Created" value={new Date(item.createdAt).toLocaleString()} />
        <FieldRow label="Request ID" value={item.id} mono />
      </DrawerSection>
    </>
  );
}

function UserDetail({ api, item }) {
  const [shifts, setShifts] = useState(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get('/admin/shifts');
        if (cancel) return;
        const list = (r.data?.items || []).filter((s) =>
          (item.role === 'manager' && s.managerEmail === item.email) ||
          (item.role === 'worker' && s.workerEmail === item.email)
        );
        setShifts(list);
      } catch {
        if (!cancel) setShifts([]);
      }
    })();
    return () => { cancel = true; };
  }, [api, item.email, item.role]);

  const statusBadge = item.is_deleted || item.accountStatus === 'deleted'
    ? <Badge className="bg-red-100 text-red-700 border-0 text-[10.5px] font-semibold">Deleted</Badge>
    : item.emailVerified === false
    ? <Badge className="bg-amber-100 text-amber-800 border-0 text-[10.5px] font-semibold">Pending verification</Badge>
    : item.isDemo
    ? <Badge className="bg-amber-100 text-amber-800 border-0 text-[10.5px] font-semibold">Demo</Badge>
    : <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10.5px] font-semibold">Active</Badge>;

  return (
    <>
      <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
        <SheetTitle className="text-lg font-bold text-slate-900 flex items-center gap-2.5">
          <Avatar name={item.fullName || item.email} />
          <span>{item.fullName || item.email}</span>
        </SheetTitle>
        <SheetDescription className="text-[12px] text-slate-500 capitalize">
          {item.role} · Joined {item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
        </SheetDescription>
      </SheetHeader>
      <DrawerSection title="Status">
        <div className="flex flex-wrap gap-2">{statusBadge}</div>
      </DrawerSection>
      <DrawerSection title="Contact">
        <FieldRow label="Email" value={<a href={`mailto:${item.email}`} className="text-[#DA291C] hover:underline">{item.email}</a>} />
        <FieldRow label="Phone" value={item.phone ? <a href={`tel:${item.phone}`} className="text-[#DA291C] hover:underline">{item.phone}</a> : null} />
      </DrawerSection>
      {item.role === 'worker' && (
        <DrawerSection title="Worker stats">
          <FieldRow label="Reliability" value={`${Math.round(item.reliabilityScore ?? 100)}%`} />
          <FieldRow label="Rating avg" value={item.ratingAverage ? `${item.ratingAverage.toFixed(1)} / 5` : null} />
          <FieldRow label="Completed shifts" value={item.completedShiftsCount ?? 0} />
          <FieldRow label="Smart ID" value={item.smartIdVerified ? 'Verified' : item.smartIdSelfDeclared ? 'Self-declared' : 'Not set'} />
        </DrawerSection>
      )}
      {item.role === 'manager' && (
        <DrawerSection title="Manager profile">
          <FieldRow label="Organisation" value={item.organizationName} />
          <FieldRow label="Operating area" value={item.operatingArea} />
          <FieldRow label="Payment locked" value={item.paymentLocked ? <span className="text-red-600 font-medium">Yes — {item.paymentLockedReason || 'Card update required'}</span> : 'No'} />
        </DrawerSection>
      )}
      <DrawerSection title={`Recent shifts ${shifts ? `(${shifts.length})` : ''}`}>
        {shifts === null ? (
          <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
        ) : shifts.length === 0 ? (
          <p className="text-[12px] text-slate-400">No shifts found for this user.</p>
        ) : (
          <ul className="space-y-1.5">
            {shifts.slice(0, 6).map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-3 py-1.5 px-2.5 rounded-lg bg-slate-50">
                <div className="min-w-0">
                  <p className="text-[12.5px] font-medium text-slate-900 truncate">{s.branchName}</p>
                  <p className="text-[10.5px] text-slate-500">{s.date} · £{s.pay}</p>
                </div>
                <ShiftStatusPill status={s.status} />
              </li>
            ))}
          </ul>
        )}
      </DrawerSection>
      <DrawerSection title="Metadata">
        <FieldRow label="User ID" value={item.id} mono />
        <FieldRow label="Onboarded" value={item.onboardingCompleted ? 'Yes' : 'No'} />
        <FieldRow label="Email verified" value={item.emailVerified ? 'Yes' : 'No'} />
      </DrawerSection>
    </>
  );
}

function ShiftDetail({ item }) {
  return (
    <>
      <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
        <SheetTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <MapPin className="h-4 w-4 text-[#0f1b3d]" />
          {item.branchName}
        </SheetTitle>
        <SheetDescription className="text-[12px] text-slate-500">
          {item.date} · <span className="font-semibold text-slate-900 tabular-nums">£{item.pay}</span>
        </SheetDescription>
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <Calendar className="h-3 w-3 text-slate-400" />
          {item.isDemo && <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">Demo</Badge>}
        </div>
      </SheetHeader>
      <DrawerSection title="Status">
        <div className="flex flex-wrap gap-2">
          <ShiftStatusPill status={item.status} />
          <PaymentStatusPill status={item.shiftPaymentStatus} />
          {item.needsReview && <Badge className="bg-red-100 text-red-700 border-0 text-[10.5px] font-semibold">Till alert</Badge>}
        </div>
      </DrawerSection>
      <DrawerSection title="Manager">
        <div className="flex items-center gap-2.5">
          <Avatar name={item.managerName || item.managerEmail} />
          <div className="min-w-0">
            <p className="text-[13px] font-medium text-slate-900 truncate">{item.managerName || '—'}</p>
            <a href={`mailto:${item.managerEmail}`} className="text-[11.5px] text-[#DA291C] hover:underline truncate">{item.managerEmail}</a>
          </div>
        </div>
      </DrawerSection>
      {item.workerEmail && (
        <DrawerSection title="Worker">
          <div className="flex items-center gap-2.5">
            <Avatar name={item.workerName || item.workerEmail} />
            <div className="min-w-0">
              <p className="text-[13px] font-medium text-slate-900 truncate">{item.workerName || '—'}</p>
              <a href={`mailto:${item.workerEmail}`} className="text-[11.5px] text-[#DA291C] hover:underline truncate">{item.workerEmail}</a>
            </div>
          </div>
        </DrawerSection>
      )}
      <DrawerSection title="Payment">
        <FieldRow label="Amount" value={item.amountCharged != null ? `£${Number(item.amountCharged).toFixed(2)}` : null} />
        <FieldRow label="Charged at" value={item.chargedAt ? new Date(item.chargedAt).toLocaleString() : null} />
        <FieldRow label="Payment ID" value={item.payment_id} mono />
      </DrawerSection>
      <DrawerSection title="Timeline">
        <FieldRow label="Created" value={item.createdAt ? new Date(item.createdAt).toLocaleString() : null} />
        <FieldRow label="Updated" value={item.updatedAt ? new Date(item.updatedAt).toLocaleString() : null} />
        <FieldRow label="Shift ID" value={item.id} mono />
      </DrawerSection>
    </>
  );
}

